// ============================================================
// W'ILY'S PIER
// A cosmic boardwalk floating over an ocean of void and stars.
// The master hub of the Illegal Empire. One login. All games.
//
// Map: 56×20 tiles. Main spine runs east-west.
// District wings branch north and south into the void.
//
// The sign says "THE UNSTABLE PIERRE."
// W'ily insists that's a typo. Nobody has the heart to tell him.
//
// This build: THE BOARDWALK PASS. Every wing is now a proper
// storefront on the pier — marquee signboards with chasing bulbs,
// striped awnings, glowing windows, buzzing neon. The spine gets
// lampposts, railings, barrels and crates; a grand gate sign rises
// from the Khao sea by the entrance; a ferris wheel turns slowly
// in the far void; void-gulls cross the sky and fireflies drift
// over the planks. Something dark now stands at the mystery spot.
// Zero logic changes — visuals, chrome, and keyframes only.
// W'ily's sparkles and Wyllie's tent untouched, as decreed.
// ============================================================

import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from "react";
import { ref, set, onValue, remove, update } from "firebase/database";
import { db } from "./firebase";
import { SD, UI, uiChip } from "./theme";
import { PlayerEntity, usePlayerAnimation, MobileDpad, isMobile } from "./PlayerEntity";
import { ProximityBubble, getNearby, getNearbyPlayer } from "./ProximitySystem";
import { calcCamOffset, isPortrait as engineIsPortrait } from "./ZoneEngine";
import {
  listenToProfile,
  listenToWallet,
  CURRENCIES,
  DC_ID,
  tintFor,
} from "./progression";
import CrestExchange from "./CrestExchange";
import Mark from "./Mark";
import MarkEditor from "./MarkEditor";
import { ChatSystem, useChatSystem } from "./ChatSystem";
import {
  usePartySystem, PartyHUD, PartyInviteBanner,
  ReadyUpModal, withPartyParam,
} from "./PartySystem";
import { setSocialHidden } from "./ieSocial";   // see THE SUPPRESSION SIGNAL below
import PierHome from "./PierHome";
import PierBazaar from "./PierBazaar";
// THE MARKETPLACE — Mystic Ally's counter. The Bazaar is players
// selling to players; this is a SHOP, with a shopkeeper, stocked on
// THE THREE SHELVES (IE_CANON_PRIME §2.15).
import PierMarketplace from "./PierMarketplace";
import { SHELVES } from "./markVariants";
// THE 3D PIER. A RENDERER, not a game — it takes the same world data
// and the same refs the 2D view uses and paints them in three
// dimensions. Game logic, Firebase, party, chat and the whole HUD are
// untouched by it.
import Pier3D from "./Pier3D";
// ⭐ THE PLAN — where everything on the boardwalk stands, and the audit that
// proves it is walkable. Pure data, so node can check it (verify/boardwalk.mjs).
import { resolveCompanionId } from "./pierFolk3D";   // the SAME companion as Terror
import {
  COLS, ROWS, T, GAME_PORTALS, HALL_FRONT, FRONTS, INTERACTABLES, PIER_PROPS, SPAWN, GATE, SQUARES, SPINE, ALLEYS, RECRUITS,
  MAP, isWalkable, LAMPPOSTS, SOLIDS, blockedBySolid, auditBoardwalk,
} from "./pierPlan";
import { findPath } from "./pierPath";   // ⭐ THE AGENT SEAT's legs (2026-09-26)

import { rotateInput, resetGroundForward, VIEWS, VIEW_ORDER, getView, isPosed, chooseView, resetCamera, subscribe as subscribeCamera } from "./pierCamera";
// ⭐ THE MENU BACKDROP (OW-46, 2026-10-21) — settings-type chrome lives in it.
// The Pier only hands it rows and chips; MenuBackdrop.js (index.js) draws it.
import { useBackdropItems, useBackdropChips, useBackdropOpen, useBackdropActive, setBackdropHidden, useBackdropTabs, useBand, useBandReserve, railPanelStyle } from "./ieBackdrop";
import { BandPortal } from "./MenuBackdrop";
import { listenToLedger } from "./bazaar";
// ⭐ THE MINERIUM — the rest of a holding. The Hall of Deeds is its
// inside; this is the yard, the farm, the sky and the companion. Its own
// screen, its own renderer, its own engine — this file only mounts it.
import MineriumScreen from "./MineriumScreen";
import { ensureDawgsRegistered, UD_ID, UD_ROW } from "./upDawgs";
import { auditCatalogue, ITEMS as MINERIUM_ITEMS, KIND as MINERIUM_KIND } from "./mineriumCatalogue";
import { auditPropArt } from "./mineriumProps";
import { auditCompanions } from "./mineriumCompanion";
// THE REPRESENTATIVES — somebody at every portal with work, paid in
// Dawgs by the Pier on your return. Pierre only ever READS the other
// game, so no partner title changes by one line (pierQuests.js).
import PierQuestBoard from "./PierQuestBoard";
// ⭐ AEMILIA — the Empire's guide (mounted in index.js; this screen only tells her
// what it can open and when to step aside). Master: gamemaster/engine/AemiliaGuide.js
import { useAemiliaPlaces, useAemiliaOpen, setAemiliaHidden } from "./AemiliaGuide";

// ════════════════════════════════════════════════════════════════════
// ⭐ THE AGENT SEAT — `?agent=1` (2026-09-26)
// ════════════════════════════════════════════════════════════════════
// caP'n: "I just wanna see if they can walk the unstable Pierre and chat."
// A program on HIS PC (Lyraei's brain, LOCAL AI STACK/IE Seat) opens the
// Pier signed in as her own account, with ?agent=1, and plays through
// window.__ieAgent — read the world, pick a place, walk there, talk.
//
// ⚑ WITHOUT ?agent=1 NOTHING HERE RUNS. No API, no steering, no extra
//   work in the loop beyond one const check. Normal play is untouched.
// ⚑ A HUMAN ALWAYS WINS. Any movement key cancels the seat's route on
//   the spot, so "take over" is just pressing W in her window.
// ⚑ SHE PLAYS BY THE SAME RULES. Her legs are the same integrator, the
//   same speed, the same walkability and wall-slide as everybody's; her
//   words go through the same chat path. She reads only what a player
//   on this screen could see — never another player's private data.
// ⚑ SCAFFOLDING. Doors, shops, the Hall and the Crest Well are not open
//   to the seat yet; interact() only talks (W'ily, the sign, recruits).
const AGENT_SEAT = (() => {
  try { return new URLSearchParams(window.location.search).get("agent") === "1"; }
  catch { return false; }
})();

// ─────────────────────────────────────────────────────────────
// CONFIG
// ─────────────────────────────────────────────────────────────

// ⚠ THE POINTER LAW (2026-08-22) — TOUCH ALONE IS NOT "MOBILE".
// `navigator.maxTouchPoints > 0` is true on every touchscreen laptop and
// 2-in-1, so this said "phone" on caP'n's PC. In OrientationLock it was a
// hard lockout (the rotate curtain over a monitor that cannot rotate);
// here it is thumb controls and phone layout handed to someone with a
// mouse. `(pointer: coarse)` asks about the PRIMARY pointer instead of
// what the hardware merely supports: phone/tablet coarse, touchscreen PC
// fine. Touch is still required, so mouse-only desktops are unchanged.
// Full reasoning lives once, on isMobile() in OrientationLock.js.
const isPortrait = () => engineIsPortrait() && isMobile();

function calcTile() {
  const vh = window.innerHeight;
  if (isPortrait()) return 40;
  const byH = Math.floor(vh / 14);
  return Math.max(36, Math.min(64, byH));
}


// ─────────────────────────────────────────────────────────────
// KEYFRAMES — imperative, id-guarded, into document.head.
// (Never JSX <style> blocks — the J'aeonxi foil freeze trap.)
// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// ⭐ THE PIER'S ART IS EXPORTED, AND THAT IS THE WHOLE DESIGN
// ─────────────────────────────────────────────────────────────
// caP'n, 2026-08-25: "the NPCs should be 2D as well, and the buildings
// and everything else 2D as well — just the 3D camera as it is now."
//
// So the 3D pier does NOT model the boardwalk. It mounts THIS ART, the
// same components the 2D pier draws, standing up in the world in front
// of a 3D camera. There is one W'ily, one Crest Well, one storefront —
// not a 2D one and a 3D impression of it. THE ONE FACE COROLLARY,
// applied to a whole town.
//
// ⚠ Pier3D.jsx imports these, and Pier3D is imported BY this file — a
// module CYCLE, deliberately. It is safe for exactly one reason: every
// name below is a hoisted `function` declaration, so the binding exists
// before either module's body runs, and Pier3D only ever CALLS them at
// render time. Do not convert any of them to `const X = () => …`; a
// const in a cycle is in the temporal dead zone and this breaks
// silently, at mount, with a ReferenceError nobody expects.
// ─────────────────────────────────────────────────────────────

export function ensureKeyframes() {
  const ID = "pier-boardwalk-pass-keyframes";
  if (document.getElementById(ID)) return;
  // clear the previous pass's block if a hot-reload left it behind
  const old = document.getElementById("pier-art-pass-keyframes");
  if (old) old.remove();
  const el = document.createElement("style");
  el.id = ID;
  el.textContent = `
    @keyframes bubbleFloat { from{transform:translateX(-50%) translateY(0px)} to{transform:translateX(-50%) translateY(-5px)} }
    @keyframes pierPortalSpin    { from{transform:rotate(0deg)}   to{transform:rotate(360deg)} }
    @keyframes pierPortalSpinRev { from{transform:rotate(360deg)} to{transform:rotate(0deg)} }
    @keyframes pierOrbit {
      from { transform: rotate(0deg)   translateX(var(--orbitR, 30px)) rotate(0deg); }
      to   { transform: rotate(360deg) translateX(var(--orbitR, 30px)) rotate(-360deg); }
    }
    @keyframes pierMoteRise {
      0%   { transform: translateY(0);     opacity: 0; }
      15%  { opacity: 0.9; }
      80%  { opacity: 0.5; }
      100% { transform: translateY(-34px); opacity: 0; }
    }
    @keyframes pierRipple {
      0%   { transform: scale(0.35); opacity: 0.8; }
      100% { transform: scale(1.25); opacity: 0; }
    }
    @keyframes pierBulbTwinkle { from { opacity: 0.45; } to { opacity: 1; } }
    @keyframes pierFlicker {
      0%, 100% { opacity: 0.9; }
      8%  { opacity: 0.5; }
      12% { opacity: 1; }
      40% { opacity: 0.75; }
      62% { opacity: 1; }
      78% { opacity: 0.6; }
    }
    @keyframes pierHover { from { transform: translateY(0); } to { transform: translateY(-4px); } }
    @keyframes pierWordmarkGlow {
      from { text-shadow: 0 0 6px rgba(167,139,250,0.25); }
      to   { text-shadow: 0 0 14px rgba(167,139,250,0.6), 0 0 30px rgba(34,211,238,0.25); }
    }
    @keyframes pierNeon {
      0%, 100% { opacity: 1; }
      3%  { opacity: 0.4; }
      6%  { opacity: 1; }
      42% { opacity: 0.85; }
      45% { opacity: 0.3; }
      47% { opacity: 1; }
      80% { opacity: 0.92; }
    }
    @keyframes pierAwningSway {
      from { transform: skewX(-0.7deg); }
      to   { transform: skewX(0.7deg); }
    }
    @keyframes pierSignSway {
      from { transform: rotate(-4deg); }
      to   { transform: rotate(4deg); }
    }
    @keyframes pierPennantWave {
      from { transform: rotate(-7deg) scaleY(0.94); }
      to   { transform: rotate(7deg)  scaleY(1.06); }
    }
    @keyframes pierWindowGlow {
      from { opacity: 0.55; }
      to   { opacity: 1; }
    }
    @keyframes pierFirefly {
      0%   { transform: translate(0px, 0px); opacity: 0; }
      12%  { opacity: 0.9; }
      50%  { transform: translate(var(--fx, 26px), var(--fy, -18px)); opacity: 0.45; }
      88%  { opacity: 0.85; }
      100% { transform: translate(calc(var(--fx, 26px) * 0.3), calc(var(--fy, -18px) * 1.7)); opacity: 0; }
    }
    @keyframes pierGullX {
      from { transform: translateX(-80px); }
      to   { transform: translateX(var(--gullSpan, 2400px)); }
    }
    @keyframes pierGullY {
      from { transform: translateY(0px) scaleX(1); }
      to   { transform: translateY(-14px) scaleX(0.92); }
    }
    @keyframes pierLampFlicker {
      0%, 100% { opacity: 0.95; }
      7%  { opacity: 0.75; }
      11% { opacity: 1; }
      53% { opacity: 0.85; }
      88% { opacity: 1; }
    }
    @keyframes pierWispOrbit {
      from { transform: rotate(0deg)   translateX(var(--wispR, 14px)); opacity: 0.15; }
      50%  { opacity: 0.7; }
      to   { transform: rotate(360deg) translateX(var(--wispR, 14px)); opacity: 0.15; }
    }
  `;
  document.head.appendChild(el);
}


// The 3D interaction pill's skin. One definition — a second copy is how
// two screens stop looking like one product.
const pill3D = (color) => ({
  background: `linear-gradient(135deg, ${color}22, rgba(4,2,14,.95))`,
  border: `1px solid ${color}88`, borderRadius: 8,
  padding: "8px 14px", cursor: "pointer",
  color: "#f0e6d0", fontSize: 10, letterSpacing: 1.2,
  fontFamily: SD.fontDisplay, whiteSpace: "nowrap",
  boxShadow: `0 6px 22px #000a, 0 0 14px ${color}33`,
});

// the plan audits itself at every dev boot (pierPlan.js)
if (process.env.NODE_ENV === "development") auditBoardwalk();


// ─────────────────────────────────────────────────────────────
// W'ILY DIALOGUE — the pier keeper's voice.
// ─────────────────────────────────────────────────────────────
// W'ily is the soul of the pier: proud, a little grandiose, quietly aware
// that the void beneath him is watching. He insists "THE UNSTABLE PIERRE" is
// a typo and would very much like everyone to forget the original name — but
// it slips out sometimes (canon: ~15%, per the locked architecture).
//
// Every visit assembles a FRESH little monologue so he never repeats himself:
//   a context-aware GREETING  (by progress + time of day)
//   + a rotating MUSING       (his thoughts on the void, the pier, memory…)
//   + occasionally a NUDGE     (toward the Bazaar or your Hall)
//   or, ~15% of the time, a NAME-SLIP instead of the musing.
//
// This is pure line generation — no logic, no writes. Feeds the same
// DialoguePanel (now a typewriter) as before; call signature unchanged.
// ─────────────────────────────────────────────────────────────

function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

// Greetings scale with how far the player has come.
function wilyGreeting(profile) {
  const crests = profile?.divineCrests || 0;
  const wins   = profile?.totalWins || 0;
  const hour   = new Date().getHours();

  // Time-of-day flavor (he is, of course, always here).
  const timeNote =
    hour < 5  ? pick([
      "The pier keeps strange hours. So do you, it seems.",
      "Late, or early — hard to tell out here over the void.",
    ]) :
    hour < 12 ? pick([
      "Morning light on the planks. The good kind of day.",
      "You're up with the tide. I respect that.",
    ]) :
    hour < 18 ? pick([
      "A fine afternoon to walk the boards.",
      "The lights aren't lit yet, but the pier's awake.",
    ]) :
    pick([
      "Evening suits the pier. Watch the string lights come on.",
      "The void goes velvet after dark. My favorite hour.",
    ]);

  // Newcomer — the full introduction (keeps the beloved sign gag).
  if (wins === 0 && crests <= 700) return [
    "Welcome to the Unstable—",
    "…",
    "Welcome to W'ily's Pier.",
    "I am W'ily. The proprietor. The visionary.",
    "Don't worry about what the sign says. It's… intentional.",
    "Each wing leads somewhere different. You'll figure it out.",
  ];

  // Wealthy — he notices, and pretends he isn't asking you to spend.
  if (crests > 2000) return [
    `${crests.toLocaleString()} Crests. I see you've been busy.`,
    timeNote,
    "Spend some. The pier needs maintenance.",
    "…I mean, enjoy the games. Obviously.",
  ];

  // Veteran — he's watched you a while, and so has the thing below.
  if (wins > 20) return [
    "You've walked these planks more than once.",
    "I've watched every lap. So has the void beneath us.",
    timeNote,
  ];

  // Returning regular.
  return [
    pick([
      "Welcome back to W'ily's Pier.",
      "Ah — you again. Good.",
      "The pier remembers regulars. So do I.",
    ]),
    timeNote,
  ];
}

// A deep well of musings — his character, one thought at a time.
const WILY_MUSINGS = [
  ["The void beneath the planks isn't empty.", "I've stopped looking down. You should too."],
  ["Every wing is a doorway to somewhere else.", "None of them lead back to who you were."],
  ["People ask if the pier is safe.", "I tell them it's *stable enough*. That's honest."],
  ["I built this place on nothing.", "Literally nothing. Look down. …Actually, don't."],
  ["The string lights? I strung every one myself.", "Don't ask what I strung them *to*."],
  ["Some nights the whole pier hums.", "I like to think it's singing. It's probably not."],
  ["The games take Crests. The Crests come back.", "Round and round. That's an economy, they tell me."],
  ["You can trade almost anything here.", "Almost. Some things the void keeps."],
  ["I've had many names for this place.", "This is the one that stuck. Mostly."],
  ["Careful near the edges.", "The planks are sound. The *drop* is the issue."],
  ["A pier should smell of salt and old wood.", "This one smells of ozone and something older. You get used to it."],
  ["Every trophy in your hall was earned.", "The void doesn't hand out participation prizes."],
  ["I don't sleep. The pier doesn't let me.", "Someone has to keep the lights on."],
  ["Watch the clouds — gold ones and teal ones.", "They never touch. I've watched for years. They never touch."],
  // The Emperium's Jewel (caP'n, 2026-09-26): it powers the pier; with the Mark no one can die or
  // fight here, and no one NEEDS to breathe, eat or sleep, though doing it still feels good.
  // That is why nobody wears a breathing suit on the boards. Canon: PIERRE_PRIME.md §5.
  ["Every Emperium needs a heart.", "Mine sits under the planks. The Jewel. That's the hum you hear."],
  ["Nobody dies on my pier. Nobody fights, either.", "Not a rule. The Jewel just won't have it. Your Mark sees to the rest."],
  ["You don't need to breathe here. Or eat. Or sleep.", "Do it anyway. It still feels nice."],
  ["Leave the helmet at the gate, if you came in one.", "The Jewel does the breathing now. You just enjoy the air."],
];

// Nudges — occasional, gentle, toward the market or the hall.
const WILY_NUDGES = [
  ["The Bazaar's just down the boardwalk.", "The market square. Watch it a while — trade never sleeps here."],
  ["Been to your Hall of Deeds?", "Every game you touch leaves something on the walls. Go look."],
  ["The Crest Well takes coins and gives Crests.", "Or the other way round. My cut is small. Ish."],
  ["If you've collected art, hang it.", "Your hall has frames waiting. A wall of your own making."],
];

// Name-slips — the canon ~15% moment, in several flavors.
const WILY_SLIPS = [
  [
    "The pier is… unstable today.",
    "Always somewhat unstable. That's part of the cha—",
    "…I mean. W'ily's Pier. That's what it's called.",
    "That's what I've always called it.",
  ],
  [
    "Welcome to the Unstable Pier—",
    "*W'ily's.* W'ily's Pier.",
    "The tongue slips. The name does not. Moving on.",
  ],
  [
    "You'll like it here at the Unstable—",
    "…",
    "You'll like it here. Full stop. No name needed.",
  ],
];

function getWilyDialogue(profile) {
  const lines = [...wilyGreeting(profile)];

  // ~15% of visits: he slips instead of musing (the canon easter egg).
  if (Math.random() < 0.15) {
    return lines.concat(pick(WILY_SLIPS));
  }

  // Otherwise: a musing, and ~1-in-3 times a gentle nudge after it.
  lines.push(...pick(WILY_MUSINGS));
  if (Math.random() < 0.33) lines.push(...pick(WILY_NUDGES));
  return lines;
}

function getOldSignDialogue() {
  return [
    "A weathered wooden sign, bolted to the planks.",
    "The paint is chipped. Most of the letters still legible.",
    "It reads: \"THE UNSTABLE PIERRE\"",
    "A thick line has been scratched through \"PIERRE\" with what looks like a fingernail.",
    "Above it, in fresh paint: \"W'ILY\"",
    "…",
    "Someone very much wants you to forget the original name.",
  ];
}

// ─────────────────────────────────────────────────────────────
// THE UNSTABLE WORLD — what the pier actually floats over.
// A beach of Khao laps at the planks. Rivers of Anima wind
// through the void. Clouds of Anima drift high; clouds of Khao
// drift low. The sky is full of stars and colors we made up.
// Anima reads GOLD (✦ radiance). Khao reads TEAL→VIOLET (✧ churn).
// ─────────────────────────────────────────────────────────────

// Shoreline precompute — every FLOOR edge that faces the void.
// side: which direction the void lies (n/s/e/w). Computed once.
function computeShoreline() {
  const edges = [];
  const isVoidAt = (x, y) =>
    x < 0 || y < 0 || x >= COLS || y >= ROWS || MAP[y][x] === T.VOID;
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      if (MAP[r][c] !== T.FLOOR) continue;
      if (isVoidAt(c, r - 1)) edges.push({ x: c, y: r, side: "n" });
      if (isVoidAt(c, r + 1)) edges.push({ x: c, y: r, side: "s" });
      if (isVoidAt(c - 1, r)) edges.push({ x: c, y: r, side: "w" });
      if (isVoidAt(c + 1, r)) edges.push({ x: c, y: r, side: "e" });
    }
  }
  return edges;
}
const SHORELINE = computeShoreline();

function UnstableWorld({ tile }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width;
    const H = canvas.height;

    // ── Stars — dense field + a few flare stars ──
    const stars = Array.from({ length: 520 }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      r: Math.random() * 1.6 + 0.3,
      a: Math.random() * 0.9 + 0.1,
      speed: Math.random() * 0.003 + 0.001,
      phase: Math.random() * Math.PI * 2,
      // colors we could only imagine (mostly white, some tinted)
      c: (() => {
        const roll = Math.random();
        if (roll < 0.70) return "255,255,255";
        if (roll < 0.80) return "167,139,250";  // violet
        if (roll < 0.88) return "34,211,238";   // teal
        if (roll < 0.95) return "240,208,96";   // gold
        return "248,113,113";                    // rose
      })(),
    }));
    const flares = Array.from({ length: 9 }, () => ({
      x: Math.random() * W, y: Math.random() * (H * 0.9),
      r: Math.random() * 1.4 + 1.4,
      len: Math.random() * 8 + 7,
      speed: Math.random() * 0.004 + 0.002,
      phase: Math.random() * Math.PI * 2,
      c: ["240,208,96", "167,139,250", "34,211,238"][Math.floor(Math.random() * 3)],
    }));

    // ── Shooting stars — rare, brief, wished upon ──
    const shooters = [];

    // ── Nebulae — the majestic sky ──
    const nebulae = [
      { x: 0.16, y: 0.30, r: 0.36, c: "124,58,237",  a: 0.14, drift: 46 },
      { x: 0.50, y: 0.14, r: 0.30, c: "34,211,238",  a: 0.11, drift: 38 },
      { x: 0.78, y: 0.55, r: 0.32, c: "201,168,76",  a: 0.09, drift: 34 },
      { x: 0.35, y: 0.80, r: 0.28, c: "244,114,182", a: 0.08, drift: 30 },
      { x: 0.92, y: 0.20, r: 0.24, c: "248,113,113", a: 0.07, drift: 26 },
      { x: 0.06, y: 0.85, r: 0.26, c: "52,211,153",  a: 0.06, drift: 24 },
    ];

    // ── Clouds of ANIMA (gold, high) and KHAO (teal→violet, low) ──
    const animaClouds = Array.from({ length: 4 }, (_, i) => ({
      x: Math.random() * W, y: H * (0.06 + Math.random() * 0.14),
      r: 50 + Math.random() * 70,
      drift: 0.05 + Math.random() * 0.06,
      phase: i * 1.7,
    }));
    const khaoClouds = Array.from({ length: 6 }, (_, i) => ({
      x: Math.random() * W, y: H * (0.72 + Math.random() * 0.24),
      r: 45 + Math.random() * 65,
      drift: 0.08 + Math.random() * 0.08,
      phase: i * 1.3,
      mix: Math.random(), // 0 = teal, 1 = violet
    }));

    // ── Rivers of ANIMA — golden currents through the void ──
    // The middle river surfaces in the gaps between the wings.
    const rivers = [
      { baseY: H * 0.11, amp: H * 0.030, f: 0.006, f2: 0.0023, w: 5.0, speed: 1.0 },
      { baseY: H * 0.33, amp: H * 0.040, f: 0.005, f2: 0.0031, w: 4.0, speed: 0.7 },
      { baseY: H * 0.93, amp: H * 0.026, f: 0.007, f2: 0.0019, w: 5.5, speed: 1.2 },
    ];
    // glowing motes riding each river
    const riverMotes = rivers.map(() =>
      Array.from({ length: 16 }, () => ({
        u: Math.random(),                       // 0..1 along the width
        v: Math.random() * 2 - 1,               // offset across the river
        sp: 0.0006 + Math.random() * 0.0009,
        r: Math.random() * 1.4 + 0.7,
      }))
    );
    const riverY = (rv, x, t) =>
      rv.baseY +
      rv.amp * Math.sin(x * rv.f + t * rv.speed) +
      rv.amp * 0.6 * Math.sin(x * rv.f2 - t * 0.7 * rv.speed);

    let frame = 0;
    let raf;
    function draw() {
      ctx.clearRect(0, 0, W, H);
      const t = frame * 0.01;

      // ── Sky base — deep and unstable ──
      const grad = ctx.createLinearGradient(0, 0, W * 0.3, H);
      grad.addColorStop(0.00, "#0b0020");
      grad.addColorStop(0.30, "#050014");
      grad.addColorStop(0.55, "#02081f");
      grad.addColorStop(0.80, "#0c0428");
      grad.addColorStop(1.00, "#120336");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);

      // ── Nebulae — drifting, breathing ──
      nebulae.forEach((n, i) => {
        const dx = Math.sin(t * 0.3 + i * 1.3) * n.drift;
        const dy = Math.cos(t * 0.24 + i * 1.7) * (n.drift * 0.6);
        const breathe = 1 + 0.10 * Math.sin(t * 0.4 + i * 2.1);
        const g = ctx.createRadialGradient(
          n.x * W + dx, n.y * H + dy, 0,
          n.x * W + dx, n.y * H + dy, n.r * W * breathe
        );
        g.addColorStop(0, `rgba(${n.c},${n.a})`);
        g.addColorStop(0.6, `rgba(${n.c},${n.a * 0.35})`);
        g.addColorStop(1, "transparent");
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, W, H);
      });

      // ── Stars ──
      stars.forEach(s => {
        const a = s.a * (0.45 + 0.55 * Math.sin(frame * s.speed + s.phase));
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${s.c},${a})`;
        ctx.fill();
      });
      // flare stars — four-point glints
      flares.forEach(s => {
        const a = 0.25 + 0.75 * Math.abs(Math.sin(frame * s.speed + s.phase));
        ctx.strokeStyle = `rgba(${s.c},${a * 0.7})`;
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.moveTo(s.x - s.len, s.y); ctx.lineTo(s.x + s.len, s.y);
        ctx.moveTo(s.x, s.y - s.len); ctx.lineTo(s.x, s.y + s.len);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${s.c},${a})`;
        ctx.fill();
      });

      // ── THE FAR WHEEL — a ferris wheel turning slowly in the void,
      // an attraction on some other pier nobody has found yet. ──
      {
        const cx = W * 0.058, cy = H * 0.205;
        const R  = Math.min(W * 0.05, H * 0.185);
        const ang = t * 0.10;
        // soft halo behind the wheel
        const halo = ctx.createRadialGradient(cx, cy, 0, cx, cy, R * 2.1);
        halo.addColorStop(0, "rgba(167,139,250,0.10)");
        halo.addColorStop(1, "transparent");
        ctx.fillStyle = halo;
        ctx.fillRect(cx - R * 2.1, cy - R * 2.1, R * 4.2, R * 4.2);
        // support legs, fading into the dark below
        const legGrad = ctx.createLinearGradient(0, cy, 0, cy + R * 2.6);
        legGrad.addColorStop(0, "rgba(90,70,150,0.55)");
        legGrad.addColorStop(1, "transparent");
        ctx.strokeStyle = legGrad;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(cx, cy); ctx.lineTo(cx - R * 0.85, cy + R * 2.5);
        ctx.moveTo(cx, cy); ctx.lineTo(cx + R * 0.85, cy + R * 2.5);
        ctx.stroke();
        // rim — double ring
        ctx.strokeStyle = "rgba(167,139,250,0.40)";
        ctx.lineWidth = 1.6;
        ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2); ctx.stroke();
        ctx.strokeStyle = "rgba(167,139,250,0.18)";
        ctx.beginPath(); ctx.arc(cx, cy, R * 0.88, 0, Math.PI * 2); ctx.stroke();
        // spokes + cabins
        const CABIN_COLORS = ["240,208,96", "34,211,238", "167,139,250", "244,114,182"];
        for (let i = 0; i < 8; i++) {
          const a = ang + (i / 8) * Math.PI * 2;
          const sx = cx + Math.cos(a) * R, sy = cy + Math.sin(a) * R;
          ctx.strokeStyle = "rgba(120,100,190,0.30)";
          ctx.lineWidth = 1;
          ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(sx, sy); ctx.stroke();
          // cabin — swings gently, stays upright, glows its color
          const cc = CABIN_COLORS[i % 4];
          const glow = 0.45 + 0.35 * Math.sin(t * 1.4 + i * 1.9);
          ctx.fillStyle = `rgba(${cc},${glow})`;
          ctx.beginPath();
          ctx.arc(sx, sy + 3, 2.4, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = `rgba(${cc},${glow * 0.4})`;
          ctx.beginPath();
          ctx.arc(sx, sy + 3, 4.4, 0, Math.PI * 2);
          ctx.stroke();
        }
        // hub light
        ctx.fillStyle = `rgba(240,208,96,${0.5 + 0.4 * Math.sin(t * 0.9)})`;
        ctx.beginPath(); ctx.arc(cx, cy, 2.6, 0, Math.PI * 2); ctx.fill();
      }

      // ── Shooting stars — a wish every few seconds ──
      if (Math.random() < 0.006 && shooters.length < 2) {
        shooters.push({
          x: Math.random() * W, y: Math.random() * H * 0.5,
          vx: 4 + Math.random() * 4, vy: 1.5 + Math.random() * 1.5,
          life: 1,
          c: Math.random() < 0.5 ? "240,208,96" : "255,255,255",
        });
      }
      for (let i = shooters.length - 1; i >= 0; i--) {
        const s = shooters[i];
        s.x += s.vx; s.y += s.vy; s.life -= 0.02;
        if (s.life <= 0 || s.x > W + 40) { shooters.splice(i, 1); continue; }
        const g = ctx.createLinearGradient(s.x - s.vx * 9, s.y - s.vy * 9, s.x, s.y);
        g.addColorStop(0, `rgba(${s.c},0)`);
        g.addColorStop(1, `rgba(${s.c},${0.85 * s.life})`);
        ctx.strokeStyle = g;
        ctx.lineWidth = 1.4;
        ctx.beginPath();
        ctx.moveTo(s.x - s.vx * 9, s.y - s.vy * 9);
        ctx.lineTo(s.x, s.y);
        ctx.stroke();
      }

      // ── Clouds of ANIMA — soft gold, drifting high ──
      animaClouds.forEach((c, i) => {
        c.x += c.drift;
        if (c.x - c.r > W) c.x = -c.r;
        const puff = 1 + 0.08 * Math.sin(t * 0.6 + c.phase);
        const g = ctx.createRadialGradient(c.x, c.y, 0, c.x, c.y, c.r * puff);
        g.addColorStop(0, "rgba(240,208,96,0.10)");
        g.addColorStop(0.55, "rgba(201,168,76,0.05)");
        g.addColorStop(1, "transparent");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.ellipse(c.x, c.y, c.r * 1.5 * puff, c.r * 0.55 * puff, 0, 0, Math.PI * 2);
        ctx.fill();
      });

      // ── Clouds of KHAO — teal→violet churn, drifting low ──
      khaoClouds.forEach((c, i) => {
        c.x -= c.drift;
        if (c.x + c.r < 0) c.x = W + c.r;
        const churn = 1 + 0.14 * Math.sin(t * 1.1 + c.phase);
        const R = Math.round(34 + c.mix * 133);   // 34→167
        const G = Math.round(211 - c.mix * 72);   // 211→139
        const B = Math.round(238 + c.mix * 12);   // 238→250
        const g = ctx.createRadialGradient(c.x, c.y, 0, c.x, c.y, c.r * churn);
        g.addColorStop(0, `rgba(${R},${G},${B},0.11)`);
        g.addColorStop(0.6, `rgba(${R},${G},${B},0.05)`);
        g.addColorStop(1, "transparent");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.ellipse(c.x, c.y, c.r * 1.4 * churn, c.r * 0.6 * churn, 0, 0, Math.PI * 2);
        ctx.fill();
      });

      // ── Rivers of ANIMA — golden currents, layered glow ──
      rivers.forEach((rv, ri) => {
        // glow pass → bright core pass
        [
          { w: rv.w * 3.2, a: 0.05 }, { w: rv.w * 1.7, a: 0.12 }, { w: rv.w * 0.8, a: 0.30 },
        ].forEach(layer => {
          ctx.strokeStyle = `rgba(240,208,96,${layer.a})`;
          ctx.lineWidth = layer.w;
          ctx.lineCap = "round";
          ctx.beginPath();
          for (let x = -10; x <= W + 10; x += 14) {
            const y = riverY(rv, x, t);
            if (x === -10) ctx.moveTo(x, y); else ctx.lineTo(x, y);
          }
          ctx.stroke();
        });
        // motes riding the current
        riverMotes[ri].forEach(m => {
          m.u += m.sp;
          if (m.u > 1) m.u -= 1;
          const mx = m.u * W;
          const my = riverY(rv, mx, t) + m.v * rv.w * 1.4;
          const a = 0.35 + 0.5 * Math.sin(t * 3 + m.u * 40);
          ctx.beginPath();
          ctx.arc(mx, my, m.r, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255,236,170,${Math.max(0, a)})`;
          ctx.fill();
        });
      });

      // ── THE BEACH OF KHAO — surf lapping at every plank edge ──
      // Two bands: bright surf-line hugging the pier, fainter wash beyond.
      SHORELINE.forEach(e => {
        const px = e.x * tile, py = e.y * tile;
        const wave = Math.sin(t * 2.2 + (e.x + e.y) * 0.55);
        const reach = tile * (0.35 + 0.22 * wave);          // tide reaches in & out
        const aNear = 0.16 + 0.10 * wave;
        const aFar  = 0.06 + 0.04 * Math.sin(t * 1.6 + (e.x - e.y) * 0.7);
        // hue churns between teal and violet along the shore
        const mix = 0.5 + 0.5 * Math.sin(t * 0.9 + e.x * 0.35 + e.y * 0.5);
        const R = Math.round(34 + mix * 133);
        const G = Math.round(211 - mix * 72);
        const B = Math.round(238 + mix * 12);
        ctx.fillStyle = `rgba(${R},${G},${B},${Math.max(0.03, aNear)})`;
        if (e.side === "n")      ctx.fillRect(px, py - reach, tile, reach);
        else if (e.side === "s") ctx.fillRect(px, py + tile, tile, reach);
        else if (e.side === "w") ctx.fillRect(px - reach, py, reach, tile);
        else                     ctx.fillRect(px + tile, py, reach, tile);
        ctx.fillStyle = `rgba(${R},${G},${B},${Math.max(0.02, aFar)})`;
        const far = reach * 1.9;
        if (e.side === "n")      ctx.fillRect(px, py - far, tile, far - reach);
        else if (e.side === "s") ctx.fillRect(px, py + tile + reach, tile, far - reach);
        else if (e.side === "w") ctx.fillRect(px - far, py, far - reach, tile);
        else                     ctx.fillRect(px + tile + reach, py, far - reach, tile);
        // foam sparkle — occasional glint where surf meets wood
        if (((e.x * 7 + e.y * 13 + frame) % 97) === 0) {
          const fx = e.side === "w" ? px - 2 : e.side === "e" ? px + tile + 2
                   : px + tile * (0.2 + 0.6 * Math.abs(Math.sin(e.x * 3.7 + t)));
          const fy = e.side === "n" ? py - 2 : e.side === "s" ? py + tile + 2
                   : py + tile * (0.2 + 0.6 * Math.abs(Math.cos(e.y * 2.9 + t)));
          ctx.beginPath();
          ctx.arc(fx, fy, 1.3, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(255,255,255,0.7)";
          ctx.fill();
        }
      });

      frame++;
      raf = requestAnimationFrame(draw);
    }
    draw();
    return () => cancelAnimationFrame(raf);
  }, [tile]);
  return (
    <canvas
      ref={canvasRef}
      width={COLS * tile}
      height={ROWS * tile}
      style={{ position: "absolute", left: 0, top: 0, zIndex: 0 }}
    />
  );
}

// ─────────────────────────────────────────────────────────────
// PIER PLANKS — weathered wood on ONE canvas (was ~700 divs),
// plus string lights along the spine and breathing district
// auras. Same call signature as before: <PierPlanks tile pulse />
// ─────────────────────────────────────────────────────────────
function getDistrictZone(x, y) {
  if (y <= 8 && x >= 6 && x <= 16)   return "terror";
  if (y >= 13 && x >= 16 && x <= 26) return "casino";
  if (y <= 8 && x >= 28 && x <= 40)  return "sol";
  if (y <= 8 && x >= 42 && x <= 52)  return "dtd";
  return "spine";
}
const DISTRICT_COLORS = {
  terror: [124, 58, 237],
  casino: [245, 158, 11],
  sol:    [34, 211, 238],
  dtd:    [248, 113, 113],
  spine:  [139, 92, 246],
};

// Deterministic grain — same pier every visit. Weathered on purpose.
function mulberry32(seed) {
  return function () {
    seed |= 0; seed = (seed + 0x6D2B79F5) | 0;
    let z = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    z = (z + Math.imul(z ^ (z >>> 7), 61 | z)) ^ z;
    return ((z ^ (z >>> 14)) >>> 0) / 4294967296;
  };
}

// District aura rects (tile coords) — soft breathing light per wing.
const DISTRICT_AURAS = [
  { zone: "terror", x: 6,  y: 3,  w: 11, h: 6, cx: 10, cy: 5  },
  { zone: "casino", x: 16, y: 13, w: 11, h: 5, cx: 20, cy: 15 },
  { zone: "sol",    x: 28, y: 3,  w: 13, h: 6, cx: 34, cy: 5  },
  { zone: "dtd",    x: 42, y: 3,  w: 11, h: 6, cx: 46, cy: 5  },
  { zone: "market", x: 34, y: 13, w: 13, h: 5, cx: 40, cy: 15 },
];
const HOME_AURA = { x: 49, y: 8, w: 7, h: 5, cx: 40, cy: 10 };

function PierPlanks({ tile, pulse }) {
  const canvasRef = useRef(null);

  // ── The wood itself — drawn once per tile size ──
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const rand = mulberry32(56 * 20); // the pier's own seed
    const isVoidAt = (x, y) =>
      x < 0 || y < 0 || x >= COLS || y >= ROWS || MAP[y][x] === T.VOID;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        if (MAP[r][c] === T.VOID) { rand(); rand(); rand(); continue; }
        const px = c * tile, py = r * tile;

        // base board — warm dark browns, weathered variance
        const shade = 0.85 + rand() * 0.3;
        const R = Math.round(42 * shade), G = Math.round(31 * shade), B = Math.round(22 * shade);
        ctx.fillStyle = `rgb(${R},${G},${B})`;
        ctx.fillRect(px, py, tile, tile);

        // faint district stain baked into the wood
        const zone = getDistrictZone(c, r);
        const [zr, zg, zb] = DISTRICT_COLORS[zone];
        ctx.fillStyle = `rgba(${zr},${zg},${zb},${zone === "spine" ? 0.03 : 0.05})`;
        ctx.fillRect(px, py, tile, tile);

        // grain — 3 wavering horizontal strokes per board
        ctx.strokeStyle = `rgba(0,0,0,${0.12 + rand() * 0.1})`;
        ctx.lineWidth = 1;
        for (let gLine = 0; gLine < 3; gLine++) {
          const gy = py + tile * (0.22 + gLine * 0.28) + (rand() - 0.5) * 3;
          ctx.beginPath();
          ctx.moveTo(px, gy);
          ctx.bezierCurveTo(
            px + tile * 0.33, gy + (rand() - 0.5) * 4,
            px + tile * 0.66, gy + (rand() - 0.5) * 4,
            px + tile, gy + (rand() - 0.5) * 3
          );
          ctx.stroke();
        }

        // the occasional knot in the wood
        if (rand() < 0.025) {
          ctx.beginPath();
          ctx.ellipse(
            px + tile * (0.3 + rand() * 0.4), py + tile * (0.3 + rand() * 0.4),
            tile * 0.07, tile * 0.05, rand() * Math.PI, 0, Math.PI * 2
          );
          ctx.fillStyle = "rgba(0,0,0,0.28)";
          ctx.fill();
        }

        // seams — boards run in 3-column runs, rows always seamed
        ctx.fillStyle = "rgba(0,0,0,0.35)";
        if (c % 3 === 0) ctx.fillRect(px + tile - 1, py, 1.5, tile);
        ctx.fillStyle = "rgba(0,0,0,0.2)";
        ctx.fillRect(px, py + tile - 1, tile, 1);

        // nails at seam crossings
        if (c % 3 === 0) {
          ctx.beginPath();
          ctx.arc(px + tile - 3, py + 4, 1.2, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(180,170,150,0.5)";
          ctx.fill();
        }

        // starlight rim — the pier's edge catches the void's glow
        ctx.strokeStyle = "rgba(120,200,230,0.30)";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        let rim = false;
        if (isVoidAt(c, r - 1)) { ctx.moveTo(px, py + 1);        ctx.lineTo(px + tile, py + 1);        rim = true; }
        if (isVoidAt(c, r + 1)) { ctx.moveTo(px, py + tile - 1); ctx.lineTo(px + tile, py + tile - 1); rim = true; }
        if (isVoidAt(c - 1, r)) { ctx.moveTo(px + 1, py);        ctx.lineTo(px + 1, py + tile);        rim = true; }
        if (isVoidAt(c + 1, r)) { ctx.moveTo(px + tile - 1, py); ctx.lineTo(px + tile - 1, py + tile); rim = true; }
        if (rim) ctx.stroke();
      }
    }

    // ── RAILINGS — a boardwalk needs something between you and
    // the Khao. Posts and a top rail on every void-facing edge. ──
    const IN = Math.max(3, tile * 0.09);   // rail inset from the edge
    SHORELINE.forEach(e => {
      const px = e.x * tile, py = e.y * tile;
      // top rail
      ctx.strokeStyle = "rgba(112,86,56,0.9)";
      ctx.lineWidth = 2.2;
      ctx.beginPath();
      if (e.side === "n")      { ctx.moveTo(px, py + IN);        ctx.lineTo(px + tile, py + IN); }
      else if (e.side === "s") { ctx.moveTo(px, py + tile - IN); ctx.lineTo(px + tile, py + tile - IN); }
      else if (e.side === "w") { ctx.moveTo(px + IN, py);        ctx.lineTo(px + IN, py + tile); }
      else                     { ctx.moveTo(px + tile - IN, py); ctx.lineTo(px + tile - IN, py + tile); }
      ctx.stroke();
      // rail highlight — catches the string lights
      ctx.strokeStyle = "rgba(240,208,96,0.16)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      if (e.side === "n")      { ctx.moveTo(px, py + IN - 1);        ctx.lineTo(px + tile, py + IN - 1); }
      else if (e.side === "s") { ctx.moveTo(px, py + tile - IN - 1); ctx.lineTo(px + tile, py + tile - IN - 1); }
      else if (e.side === "w") { ctx.moveTo(px + IN - 1, py);        ctx.lineTo(px + IN - 1, py + tile); }
      else                     { ctx.moveTo(px + tile - IN - 1, py); ctx.lineTo(px + tile - IN - 1, py + tile); }
      ctx.stroke();
      // posts at each tile corner along the edge
      const post = (qx, qy) => {
        ctx.fillStyle = "rgba(58,42,26,0.95)";
        ctx.fillRect(qx - 1.6, qy - 2.5, 3.2, 5);
        ctx.fillStyle = "rgba(240,208,96,0.35)";
        ctx.fillRect(qx - 1.6, qy - 2.5, 3.2, 1.2);   // gold-lit cap
      };
      if (e.side === "n")      { post(px, py + IN);            post(px + tile, py + IN); }
      else if (e.side === "s") { post(px, py + tile - IN);     post(px + tile, py + tile - IN); }
      else if (e.side === "w") { post(px + IN, py);            post(px + IN, py + tile); }
      else                     { post(px + tile - IN, py);     post(px + tile - IN, py + tile); }
    });

    // ── DOCK PROPS — barrels, crates, rope coils. Hand-placed on
    // safe tiles, clear of portals, NPCs, and the main walkways. ──
    const drawBarrel = (bx, by) => {
      const w = tile * 0.42, h = tile * 0.5;
      const x0 = bx * tile - w / 2, y0 = by * tile - h / 2;
      ctx.fillStyle = "#3a2a18";
      ctx.beginPath();
      ctx.ellipse(x0 + w / 2, y0 + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "rgba(84,60,36,0.9)";
      ctx.beginPath();
      ctx.ellipse(x0 + w / 2, y0 + h * 0.38, w * 0.40, h * 0.26, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "rgba(150,140,120,0.5)";   // iron bands
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.ellipse(x0 + w / 2, y0 + h / 2, w / 2 - 1, h / 2 - 1, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.strokeStyle = "rgba(0,0,0,0.4)";
      ctx.beginPath();
      ctx.moveTo(x0 + w * 0.5, y0 + 2); ctx.lineTo(x0 + w * 0.5, y0 + h - 2);
      ctx.stroke();
    };
    const drawCrate = (bx, by) => {
      const s = tile * 0.46;
      const x0 = bx * tile - s / 2, y0 = by * tile - s / 2;
      ctx.fillStyle = "#4a3620";
      ctx.fillRect(x0, y0, s, s);
      ctx.strokeStyle = "rgba(0,0,0,0.45)";
      ctx.lineWidth = 1.4;
      ctx.strokeRect(x0 + 1, y0 + 1, s - 2, s - 2);
      ctx.beginPath();                                // cross planks
      ctx.moveTo(x0 + 2, y0 + 2); ctx.lineTo(x0 + s - 2, y0 + s - 2);
      ctx.moveTo(x0 + s - 2, y0 + 2); ctx.lineTo(x0 + 2, y0 + s - 2);
      ctx.stroke();
      ctx.fillStyle = "rgba(240,208,96,0.10)";        // lamplight kiss
      ctx.fillRect(x0, y0, s, s * 0.22);
    };
    const drawRope = (bx, by) => {
      const cx0 = bx * tile, cy0 = by * tile;
      ctx.strokeStyle = "rgba(140,112,70,0.85)";
      ctx.lineWidth = 2.4;
      for (let rr = tile * 0.06; rr <= tile * 0.2; rr += tile * 0.07) {
        ctx.beginPath();
        ctx.ellipse(cx0, cy0, rr * 1.25, rr, 0, 0, Math.PI * 2);
        ctx.stroke();
      }
    };
    const PROPS = [
      { t: "barrel", x: 6.6,  y: 3.6  },   // terror wing, back corner
      { t: "crate",  x: 15.6, y: 7.55 },   // terror wing, front edge
      { t: "crate",  x: 16.6, y: 13.6 },   // casino wing, near corner
      { t: "barrel", x: 25.5, y: 16.6 },   // casino wing, deep corner
      { t: "barrel", x: 28.6, y: 3.6  },   // sol wing
      { t: "crate",  x: 39.5, y: 7.55 },   // sol wing, front edge
      { t: "crate",  x: 42.6, y: 3.6  },   // dtd wing
      { t: "barrel", x: 51.5, y: 7.55 },   // dtd wing, front edge
      { t: "rope",   x: 30.7, y: 11.7 },   // spine, past the well
      { t: "barrel", x: 47.2, y: 8.6  },   // spine, before home
      { t: "rope",   x: 11.5, y: 11.6 },   // spine, south of terror
    ];
    PROPS.forEach(p => {
      if (p.t === "barrel") drawBarrel(p.x, p.y);
      else if (p.t === "crate") drawCrate(p.x, p.y);
      else drawRope(p.x, p.y);
    });
  }, [tile]);

  // ── String lights along the spine — every boardwalk needs them ──
  const bulbs = [];
  const BULB_COLORS = ["#f0d060", "#22d3ee", "#a78bfa", "#f472b6"];
  for (let c = 0; c < COLS; c += 2) {
    [8.12, 12.82].forEach((row, ri) => {
      const i = c / 2 + ri * 40;
      bulbs.push(
        <div key={`bulb-${c}-${ri}`} style={{
          position: "absolute",
          left: c * tile + tile * (ri === 0 ? 0.5 : 0.95),
          top: row * tile + Math.sin(c * 0.9 + ri) * 2, // the wire sags a little
          width: 5, height: 5, borderRadius: "50%",
          background: BULB_COLORS[(c / 2 + ri) % 4],
          boxShadow: `0 0 7px 1.5px ${BULB_COLORS[(c / 2 + ri) % 4]}`,
          zIndex: 3, pointerEvents: "none",
          animation: `pierBulbTwinkle ${(2.2 + (c % 5) * 0.35).toFixed(2)}s ease-in-out ${((c * 0.13 + ri) % 2).toFixed(2)}s infinite alternate`,
        }} />
      );
    });
  }

  return (
    <>
      <canvas
        ref={canvasRef}
        width={COLS * tile}
        height={ROWS * tile}
        style={{ position: "absolute", left: 0, top: 0, zIndex: 1 }}
      />
      {/* wires */}
      {[8.12, 12.82].map((row, ri) => (
        <div key={`wire-${ri}`} style={{
          position: "absolute", left: 0, top: row * tile + 2,
          width: COLS * tile, height: 1,
          background: "rgba(200,200,220,0.12)",
          zIndex: 3, pointerEvents: "none",
        }} />
      ))}
      {bulbs}
      {/* district auras — the wings breathe their colors */}
      {DISTRICT_AURAS.map(a => {
        const [R, G, B] = DISTRICT_COLORS[a.zone];
        return (
          <div key={`aura-${a.zone}`} style={{
            position: "absolute",
            left: a.x * tile, top: a.y * tile,
            width: a.w * tile, height: a.h * tile,
            background: `radial-gradient(ellipse at ${((a.cx - a.x) / a.w) * 100}% ${((a.cy - a.y) / a.h) * 100}%, rgba(${R},${G},${B},0.14) 0%, rgba(${R},${G},${B},0.05) 55%, transparent 80%)`,
            opacity: 0.5 + pulse * 0.5,
            zIndex: 2, pointerEvents: "none",
          }} />
        );
      })}
      <div style={{
        position: "absolute",
        left: HOME_AURA.x * tile, top: HOME_AURA.y * tile,
        width: HOME_AURA.w * tile, height: HOME_AURA.h * tile,
        background: `radial-gradient(ellipse at 30% 50%, rgba(240,208,96,0.10) 0%, transparent 75%)`,
        opacity: 0.5 + pulse * 0.5,
        zIndex: 2, pointerEvents: "none",
      }} />
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// GAME PORTAL VISUAL — a living rift: spinning ring, orbiting
// motes, and a pool of light spilling onto the planks below.
// ─────────────────────────────────────────────────────────────
// ⚠⚠ `x` / `y` ARE THE ORIGIN THIS DRAWS AROUND, AND THEY DEFAULT TO THE
// MAP COORDS SO THE 2D PIER IS UNCHANGED.
//
// THE BUG THIS FIXES, because it is the worst one this pier has had:
// `Pier3D`'s `Flat` wrapper mounts a 2D art component inside a 0×0 box
// whose origin IS the anchor, and hands it `x={0} y={0}` — which every
// other art component on the boardwalk honours. This one and
// `StorefrontFacade` ignored them and kept reading `portal.x` /
// `store.x` instead, so in 3D they drew `portal.x * 48` CSS pixels to
// the RIGHT of where they belonged. S'WAT WING (x:24) painted 1,104px
// off its own ring; LYRAEA WING (x:46) painted 2,160px off.
//
// ⛑ What that looked like: every ring on the plaza standing EMPTY, and
// every wing name, icon and SOON tag piled up over the open sea on the
// right of frame with no deck under them — and, because `distanceFactor`
// scales the whole wrapper, sliding around as the player walked. It read
// exactly like "the right-hand side of the pier is unlit", which is what
// it was diagnosed as for a session. It was never a light.
//
// ⛑ THE SHAPE TO REMEMBER: a component that takes an origin and then
// ignores it is not wrong until somebody mounts it somewhere new — and
// then it is wrong SILENTLY, because nothing throws when art draws in
// the wrong place. Found by adversarial review 2026-09-14.
//
// `labelled` is for the 3D pier, which paints the district name as a
// world-space sign instead — 8px DOM text at a distance factor is
// 2-4 screen pixels, which is not a label, it is grain.
export function GamePortalVisual({ portal, tile, x = portal.x, y = portal.y, labelled = true }) {
  const [frame, setFrame] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setFrame(f => (f+1) % 240), 60);
    return () => clearInterval(t);
  }, []);
  const pulse = 0.5 + 0.5 * Math.sin(frame * 0.05);
  const glow  = Math.round(25 + pulse * 28);
  const soon  = portal.status === "coming_soon";

  return (
    <div style={{
      position: "absolute",
      left: (x - 1) * tile,
      top: (y - 2) * tile,
      width: tile * 3,
      zIndex: 6,
      display: "flex", flexDirection: "column", alignItems: "center",
      pointerEvents: "none",
      // ⚠ 0.45 greyscale on brand colours that are ALREADY muted
      // (#c9b48b, #5a5a6e, #5b8a72) is what turned nineteen of the
      // twenty-three wings into identical brown-grey mush. A coming-soon
      // wing should read as DIMMER, not as COLOURLESS — the colour is
      // how a player tells one wing from another at a distance.
      filter: soon ? "grayscale(0.18) brightness(0.92)" : "none",
    }}>
      {labelled && <div style={{
        fontSize: 10, letterSpacing: 2.5, color: portal.color,
        fontFamily: SD.fontDisplay, fontWeight: 700,
        textShadow: `0 0 8px ${portal.color}, 0 0 20px ${portal.color}66`,
        opacity: 0.72 + 0.28 * pulse,
        marginBottom: 2,
      }}>{portal.districtLabel}</div>}

      <div style={{
        position: "relative",
        width: tile * 2, height: tile * 2,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {/* pool of light on the planks */}
        <div style={{
          position: "absolute", left: "50%", bottom: -tile * 0.28,
          transform: "translateX(-50%)",
          width: tile * 2.4, height: tile * 0.7,
          borderRadius: "50%",
          background: `radial-gradient(ellipse, ${portal.color}${Math.round(28 + pulse * 26).toString(16).padStart(2,"0")} 0%, transparent 70%)`,
          filter: "blur(3px)",
        }} />
        {/* the rift ring — a slow spinning band of the portal's color */}
        <div style={{
          position: "absolute", inset: tile * 0.1,
          borderRadius: "50%",
          background: `conic-gradient(from 0deg, transparent 0%, ${portal.color}55 12%, transparent 30%, transparent 50%, ${portal.color}44 62%, transparent 80%)`,
          animation: `pierPortalSpin ${soon ? 26 : 11}s linear infinite`,
          WebkitMask: "radial-gradient(circle, transparent 58%, black 62%, black 78%, transparent 82%)",
          mask: "radial-gradient(circle, transparent 58%, black 62%, black 78%, transparent 82%)",
        }} />
        {/* counter-ring, thinner, faster */}
        <div style={{
          position: "absolute", inset: tile * 0.28,
          borderRadius: "50%",
          background: `conic-gradient(from 180deg, transparent 0%, ${portal.color}66 18%, transparent 40%)`,
          animation: `pierPortalSpinRev ${soon ? 20 : 7}s linear infinite`,
          WebkitMask: "radial-gradient(circle, transparent 66%, black 70%, black 84%, transparent 88%)",
          mask: "radial-gradient(circle, transparent 66%, black 70%, black 84%, transparent 88%)",
        }} />
        {/* orbiting motes */}
        {!soon && [0, 1, 2].map(i => (
          <div key={i} style={{
            position: "absolute", left: "50%", top: "50%",
            width: 4, height: 4, marginLeft: -2, marginTop: -2,
            borderRadius: "50%",
            background: portal.color,
            boxShadow: `0 0 6px ${portal.color}`,
            animation: `pierOrbit ${5 + i * 1.6}s linear ${i * -1.9}s infinite`,
            ["--orbitR"]: `${tile * (0.75 + i * 0.12)}px`,
          }} />
        ))}
        {/* the heart of the rift */}
        <div style={{
          width: tile * 1.55, height: tile * 1.55,
          borderRadius: "50%",
          background: `radial-gradient(ellipse at 50% 70%, ${portal.color}${Math.round(34 + pulse*54).toString(16).padStart(2,"0")} 0%, ${portal.color}18 45%, transparent 72%)`,
          boxShadow: `0 0 ${glow}px ${portal.color}88, inset 0 0 22px ${portal.color}30`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: tile * 0.9,
          transition: "box-shadow 0.2s",
        }}>
          {portal.icon}
        </div>
      </div>

      {soon && (
        <div style={{ fontSize: 7, letterSpacing: 2, color: SD.starDim,
          fontFamily: SD.fontDisplay, marginTop: 2 }}>SOON</div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// CREST WELL GLOW
// ─────────────────────────────────────────────────────────────
export function CrestWellVisual({ tile, x, y }) {
  const [frame, setFrame] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setFrame(f => (f+1) % 180), 60);
    return () => clearInterval(t);
  }, []);
  const pulse = 0.4 + 0.6 * Math.sin(frame * 0.06);
  return (
    <div style={{
      position: "absolute",
      left: x * tile - tile,
      top: y * tile - tile * 0.8,
      width: tile * 3, height: tile * 2.5,
      zIndex: 4, pointerEvents: "none",
      display: "flex", justifyContent: "center", alignItems: "center",
    }}>
      {/* ambient gold pool */}
      <div style={{
        position: "absolute",
        width: tile * 2.6, height: tile * 2.6,
        borderRadius: "50%",
        background: `radial-gradient(circle, ${SD.gold}${Math.round(30 + pulse * 30).toString(16).padStart(2,"0")} 0%, transparent 60%)`,
        filter: "blur(8px)",
      }} />
      {/* the stone mouth of the well */}
      <div style={{
        position: "absolute",
        width: tile * 1.5, height: tile * 1.1,
        borderRadius: "50%",
        border: `3px solid rgba(90,75,50,0.9)`,
        boxShadow: `inset 0 0 ${12 + pulse * 10}px ${SD.gold}66, 0 3px 6px rgba(0,0,0,0.5), 0 0 ${10 + pulse * 12}px ${SD.gold}44`,
        background: `radial-gradient(ellipse at 50% 40%, ${SD.gold}22 0%, rgba(10,6,2,0.92) 65%)`,
        top: tile * 0.95,
      }} />
      {/* expanding ripple rings — Anima stirring below */}
      {[0, 1].map(i => (
        <div key={i} style={{
          position: "absolute",
          top: tile * 1.05, left: "50%",
          width: tile * 1.2, height: tile * 0.8,
          marginLeft: -tile * 0.6,
          borderRadius: "50%",
          border: `1px solid ${SD.goldBright}`,
          animation: `pierRipple 3.2s ease-out ${i * 1.6}s infinite`,
        }} />
      ))}
      {/* rising Anima motes */}
      {[0, 1, 2, 3].map(i => (
        <div key={`m${i}`} style={{
          position: "absolute",
          left: `${34 + i * 11}%`, bottom: tile * 0.3,
          width: 3.5, height: 3.5, borderRadius: "50%",
          background: SD.goldBright,
          boxShadow: `0 0 6px ${SD.goldBright}`,
          animation: `pierMoteRise ${2.6 + i * 0.5}s ease-in ${i * 0.7}s infinite`,
        }} />
      ))}
      {/* the Crest itself, hovering above the water */}
      <div style={{
        position: "relative",
        fontSize: tile * 0.8,
        filter: `drop-shadow(0 0 ${10 + pulse * 14}px ${SD.gold})`,
        opacity: 0.85 + pulse * 0.15,
        color: SD.gold,
        animation: "pierHover 3.4s ease-in-out infinite alternate",
      }}>◆</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// W'ILY SPARKLES — ambient magic orbiting him
// ─────────────────────────────────────────────────────────────
export function WilySparkles({ tile, x, y }) {
  const [sparkles] = useState(() =>
    Array.from({ length: 6 }, (_, i) => ({
      id: i,
      angle: (i / 6) * Math.PI * 2,
      radius: Math.random() * 0.4 + 0.3,
      phase: Math.random() * Math.PI * 2,
      speed: 0.015 + Math.random() * 0.01,
    }))
  );
  const [frame, setFrame] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setFrame(f => f + 1), 50);
    return () => clearInterval(t);
  }, []);

  return (
    <div style={{
      position: "absolute",
      left: x * tile,
      top: (y - 0.5) * tile,
      width: tile, height: tile,
      zIndex: 7, pointerEvents: "none",
    }}>
      {sparkles.map(s => {
        const t = frame * s.speed + s.phase;
        const orbitR = tile * s.radius;
        const px = Math.cos(s.angle + t) * orbitR + tile / 2;
        const py = Math.sin(s.angle + t) * orbitR * 0.6 + tile * 0.3;
        const opacity = 0.3 + 0.7 * Math.abs(Math.sin(t * 1.7));
        return (
          <div key={s.id} style={{
            position: "absolute",
            left: px - 2, top: py - 2,
            width: 4, height: 4,
            borderRadius: "50%",
            background: SD.purpleLight,
            boxShadow: `0 0 6px ${SD.purpleLight}`,
            opacity,
          }} />
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// OLD SIGN VISUAL — the Unstable Pierre easter egg
// ─────────────────────────────────────────────────────────────
export function OldSignVisual({ tile, x, y }) {
  return (
    <div style={{
      position: "absolute",
      left: x * tile, top: (y - 0.8) * tile,
      width: tile, height: tile,
      zIndex: 5, pointerEvents: "none",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: tile * 0.6,
      filter: "drop-shadow(0 0 4px rgba(139,115,85,0.5))",
      opacity: 0.8,
    }}>
      {/* a tired little lantern keeps the old name lit */}
      <div style={{
        position: "absolute", right: -3, top: 2,
        width: 5, height: 6, borderRadius: 2,
        background: "#ffb84d",
        boxShadow: "0 0 8px 2px rgba(255,184,77,0.7)",
        animation: "pierFlicker 4s ease-in-out infinite",
      }} />
      🪧
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// HOME ENTRANCE — a shimmering archway into your Hall of Deeds.
// ─────────────────────────────────────────────────────────────
export function HomeEntranceVisual({ tile, x, y }) {
  const [frame, setFrame] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setFrame(f => (f + 1) % 240), 60);
    return () => clearInterval(t);
  }, []);
  const pulse = 0.5 + 0.5 * Math.sin(frame * 0.05);
  const gold = "#f0d060";
  return (
    <div style={{
      position: "absolute",
      left: (x - 1) * tile,
      top: (y - 2) * tile,
      width: tile * 3,
      zIndex: 4,
      display: "flex", flexDirection: "column", alignItems: "center",
      pointerEvents: "none",
    }}>
      <div style={{
        fontSize: 8, letterSpacing: 3, color: gold,
        fontFamily: SD.fontDisplay, fontWeight: 700,
        textShadow: `0 0 8px ${gold}, 0 0 20px ${gold}55`,
        opacity: 0.6 + 0.4 * pulse, marginBottom: 2,
      }}>HOME WING</div>
      <div style={{
        position: "relative",
        width: tile * 1.7, height: tile * 2,
        borderRadius: `${tile}px ${tile}px 6px 6px`,
        border: `2px solid ${gold}${Math.round(120 + pulse * 100).toString(16).padStart(2, "0")}`,
        background: `radial-gradient(ellipse at 50% 40%, ${gold}${Math.round(20 + pulse * 30).toString(16).padStart(2, "0")} 0%, rgba(5,3,20,0.6) 70%)`,
        boxShadow: `0 0 ${18 + pulse * 22}px ${gold}55, inset 0 0 24px ${gold}22`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: tile * 0.8,
        filter: `drop-shadow(0 0 ${6 + pulse * 8}px ${gold})`,
      }}>
        {/* pillar flames — a hearth that's always lit for you */}
        {[0, 1].map(i => (
          <div key={i} style={{
            position: "absolute", bottom: tile * 0.14,
            left: i === 0 ? -4 : undefined, right: i === 1 ? -4 : undefined,
            width: 5, height: 8, borderRadius: "50% 50% 40% 40%",
            background: `linear-gradient(to top, ${gold}, #fff6d0)`,
            boxShadow: `0 0 9px 2px ${gold}aa`,
            animation: `pierFlicker ${3.1 + i * 0.9}s ease-in-out ${i * 0.6}s infinite`,
          }} />
        ))}
        {/* motes drifting out of the doorway */}
        {[0, 1, 2].map(i => (
          <div key={`hm${i}`} style={{
            position: "absolute", bottom: tile * 0.25,
            left: `${30 + i * 20}%`,
            width: 3, height: 3, borderRadius: "50%",
            background: gold, boxShadow: `0 0 5px ${gold}`,
            animation: `pierMoteRise ${3 + i * 0.7}s ease-in ${i * 1.1}s infinite`,
          }} />
        ))}
        🏛️
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// BAZAAR STALL — Wyllie's canvas tent in the commerce district.
// Warm rose lantern-light next to the Crest Well's gold.
// ─────────────────────────────────────────────────────────────

// ═════════════════════════════════════════════════════════════════════
// MYSTIC ALLY'S STALL — the Marketplace, seen from the boardwalk
// ---------------------------------------------------------------------
// Three plates hang over her counter, and they are the LAW rendered as
// set dressing: the left one is flat and honest, the middle one is
// elaborate and PERFECTLY STILL, the right one is the middle one awake.
// A player who never opens the shop still learns the three shelves by
// walking past them. That is the whole job of this component.
//
// ⚠ No PRNG calls in render (ie-3d-conversion). The mote offsets are
// module-level constants, not Math.random() in a style object.
// ═════════════════════════════════════════════════════════════════════
const ALLY_MOTES = [
  { l: 14, t: 62, d: 4.2, w: 0 }, { l: 38, t: 78, d: 5.1, w: 1.3 },
  { l: 61, t: 58, d: 3.6, w: 2.4 }, { l: 82, t: 74, d: 4.8, w: 0.7 },
];

export function MysticAllyStall({ tile, x, y }) {
  const [frame, setFrame] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setFrame(f => (f + 1) % 360), 70);
    return () => clearInterval(t);
  }, []);
  const pulse = 0.5 + 0.5 * Math.sin(frame * 0.04);

  const F = SHELVES.fable, L = SHELVES.legendary, M = SHELVES.mythical;
  const plateW = tile * 0.62, plateH = tile * 0.86;

  const plate = (shelf, i) => {
    const common = {
      width: plateW, height: plateH, position: "relative",
      overflow: "hidden", flex: "0 0 auto",
      background: "linear-gradient(158deg, #14111d, #07060c)",
      border: `1px solid ${shelf.tint}66`,
      boxShadow: `0 2px 8px #000a, inset 0 0 12px ${shelf.tint}22`,
    };
    if (shelf.id === "fable") return (
      <div key={i} style={common}>
        <div style={{
          position: "absolute", inset: 0,
          background: `linear-gradient(115deg, transparent 18%, ${shelf.tint}aa 48%, transparent 74%)`,
          mixBlendMode: "soft-light",
        }} />
      </div>
    );
    if (shelf.id === "legendary") return (
      <div key={i} style={common}>
        <div style={{
          position: "absolute", left: "-60%", top: "-90%", width: "220%", height: "300%",
          background: `repeating-conic-gradient(from 14deg, ${shelf.tint}26 0deg, ${shelf.tint}26 2deg, transparent 2deg, transparent 5deg)`,
          mixBlendMode: "overlay",
        }} />
        <div style={{
          position: "absolute", inset: 0,
          background: `radial-gradient(ellipse at 20% 0%, ${shelf.tintHi}40, transparent 62%)`,
          mixBlendMode: "screen",
        }} />
        {/* the highlight is PARKED, not sweeping. Legendary does not move. */}
        <div style={{
          position: "absolute", top: "-20%", left: "40%", width: "22%", height: "150%",
          background: `linear-gradient(90deg, transparent, ${shelf.tintHi}55, transparent)`,
          transform: "skewX(-18deg)",
        }} />
      </div>
    );
    return (
      <div key={i} style={common}>
        <div style={{
          position: "absolute", left: "-60%", top: "-90%", width: "220%", height: "300%",
          background: `repeating-conic-gradient(from 14deg, ${shelf.tint}26 0deg, ${shelf.tint}26 2deg, transparent 2deg, transparent 5deg)`,
          mixBlendMode: "overlay",
          animation: "pierPortalSpin 34s linear infinite",
        }} />
        <div style={{
          position: "absolute", inset: 0,
          background: `radial-gradient(ellipse at 20% 0%, ${shelf.tintHi}44, transparent 62%)`,
          mixBlendMode: "screen", opacity: 0.55 + pulse * 0.45,
        }} />
        {ALLY_MOTES.map((m, k) => (
          <span key={k} style={{
            position: "absolute", left: `${m.l}%`, top: `${m.t}%`,
            width: 2, height: 2, borderRadius: "50%",
            background: shelf.tintHi, boxShadow: `0 0 6px ${shelf.tintHi}`,
            "--fx": "6px", "--fy": "-20px",
            animation: `pierMoteRise ${m.d}s linear ${m.w}s infinite`,
          }} />
        ))}
      </div>
    );
  };

  return (
    <div style={{
      position: "absolute",
      left: (x - 1.5) * tile,
      top: (y - 3.1) * tile,
      width: tile * 4,
      zIndex: 4, pointerEvents: "none",
      display: "flex", flexDirection: "column", alignItems: "center",
    }}>
      {/* MARQUEE */}
      <div style={{
        background: "linear-gradient(180deg, rgba(18,12,30,.97), rgba(7,4,14,.97))",
        border: `1px solid #a78bfa${Math.round(80 + pulse * 90).toString(16).padStart(2, "0")}`,
        borderRadius: 5,
        boxShadow: `0 3px 12px #000a, 0 0 ${10 + pulse * 14}px #a78bfa33`,
        padding: `${Math.max(3, tile * .07)}px ${Math.max(6, tile * .16)}px`,
        marginBottom: tile * .12, whiteSpace: "nowrap",
      }}>
        <div style={{
          fontFamily: SD.fontDisplay, fontSize: Math.max(7, tile * .17),
          letterSpacing: 2, color: "#e9d5ff",
          textShadow: `0 0 8px #a78bfa`,
        }}>✧ THE MARKETPLACE ✧</div>
        <div style={{
          fontFamily: SD.fontMono, fontSize: Math.max(5, tile * .11),
          letterSpacing: 1.4, color: "#8b8496", textAlign: "center", marginTop: 1,
        }}>MYSTIC ALLY, PROP.</div>
      </div>

      {/* THE THREE PLATES ON DISPLAY — the law, hanging in the air */}
      <div style={{ display: "flex", gap: tile * .12, marginBottom: tile * .1 }}>
        {[F, L, M].map(plate)}
      </div>

      {/* AWNING + COUNTER */}
      <div style={{
        width: tile * 3.1, height: tile * .3,
        background: `repeating-linear-gradient(90deg, #4c1d95 0 ${tile*.26}px, #7c3aed ${tile*.26}px ${tile*.52}px)`,
        borderRadius: `${tile*.05}px ${tile*.05}px 0 0`,
        boxShadow: `0 3px 10px #000a`,
        animation: "pierAwningSway 6s ease-in-out infinite alternate",
      }} />
      <div style={{
        width: tile * 2.7, height: tile * .42,
        background: "linear-gradient(180deg, #241c31, #120d1c)",
        border: "1px solid #3a3050",
        boxShadow: `0 4px 14px #000b, inset 0 1px 0 #a78bfa22`,
      }} />
    </div>
  );
}

export function BazaarStallVisual({ tile, x, y }) {
  const [frame, setFrame] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setFrame(f => (f + 1) % 240), 60);
    return () => clearInterval(t);
  }, []);

  // Live Market Pulse — subscribe to the ledger and cycle the latest sales
  // across the marquee. This is the market square's living heartbeat, out on
  // the pier: you see trade happening before you ever walk up to the counter.
  const [sales, setSales] = useState([]);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const off = listenToLedger(setSales, 12);
    return () => off && off();
  }, []);
  useEffect(() => {
    if (sales.length <= 1) return;
    const t = setInterval(() => setTick(k => k + 1), 3200); // rotate every ~3s
    return () => clearInterval(t);
  }, [sales.length]);

  const pulse = 0.5 + 0.5 * Math.sin(frame * 0.045);
  const rose = "#f472b6";
  const current = sales.length ? sales[tick % sales.length] : null;
  // Registry-driven so a new title's coin renders on the pier signboard
  // the first time one changes hands — no edit here.
  const cur = current
    ? {
        glyph: CURRENCIES[current.currency]?.icon || "•",
        color: tintFor(current.currency || DC_ID),
      }
    : null;

  return (
    <div style={{
      position: "absolute",
      left: (x - 1) * tile,
      top: (y - 2.9) * tile,
      width: tile * 3,
      zIndex: 4,
      display: "flex", flexDirection: "column", alignItems: "center",
      pointerEvents: "none",
    }}>
      {/* ── LIVE MARKET PULSE BOARD ── a hanging signboard above the stall */}
      <div style={{
        width: tile * 3.1,
        marginBottom: tile * 0.14,
        background: "linear-gradient(180deg, rgba(20,10,26,0.96), rgba(8,4,16,0.96))",
        border: `1px solid ${rose}${Math.round(90 + pulse * 80).toString(16).padStart(2, "0")}`,
        borderRadius: 6,
        boxShadow: `0 3px 10px rgba(0,0,0,0.6), 0 0 ${10 + pulse * 12}px ${rose}33, inset 0 1px 0 ${rose}22`,
        padding: `${Math.max(3, tile * 0.08)}px ${Math.max(5, tile * 0.12)}px`,
        overflow: "hidden",
      }}>
        {/* header row */}
        <div style={{
          display: "flex", alignItems: "center", gap: 4, marginBottom: 2,
        }}>
          <div style={{
            width: 5, height: 5, borderRadius: "50%",
            background: "#4ade80", boxShadow: "0 0 5px #4ade80",
            opacity: 0.5 + 0.5 * pulse,
          }} />
          <div style={{
            fontSize: Math.max(5, tile * 0.13), letterSpacing: 2, color: rose,
            fontFamily: SD.fontDisplay, fontWeight: 900,
            textShadow: `0 0 6px ${rose}`,
          }}>MARKET PULSE · LIVE</div>
        </div>
        {/* ticker line */}
        <div style={{
          fontSize: Math.max(6, tile * 0.145),
          fontFamily: SD.fontDisplay, whiteSpace: "nowrap",
          overflow: "hidden", textOverflow: "ellipsis",
          color: SD.starSilver, lineHeight: 1.4,
        }}>
          {current ? (
            <>
              <span style={{ color: SD.starWhite, fontWeight: 900 }}>{current.title || "A piece"}</span>
              <span style={{ color: SD.starDim }}>{" → "}</span>
              <span style={{ color: cur.color, fontWeight: 900 }}>
                {cur.glyph} {Number(current.price).toLocaleString()}
              </span>
            </>
          ) : (
            <span style={{ color: SD.starDim, fontStyle: "italic" }}>
              the ledger awaits its first sale…
            </span>
          )}
        </div>
      </div>

      <div style={{
        fontSize: 8, letterSpacing: 3, color: rose,
        fontFamily: SD.fontDisplay, fontWeight: 700,
        textShadow: `0 0 8px ${rose}`,
        opacity: 0.6 + 0.4 * pulse, marginBottom: 2,
      }}>MARKET SQUARE</div>
      <div style={{
        width: tile * 2, height: tile * 2,
        borderRadius: "50% 50% 8px 8px",
        border: `2px solid ${rose}${Math.round(110 + pulse * 100).toString(16).padStart(2, "0")}`,
        background: `radial-gradient(ellipse at 50% 30%, ${rose}${Math.round(18 + pulse * 30).toString(16).padStart(2, "0")} 0%, rgba(5,3,20,0.65) 75%)`,
        boxShadow: `0 0 ${16 + pulse * 20}px ${rose}44, inset 0 0 22px ${rose}22`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: tile * 0.85,
        filter: `drop-shadow(0 0 ${5 + pulse * 8}px ${rose})`,
      }}>🎪</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// STOREFRONT FACADE — every wing is a shop on the boardwalk.
// Marquee with chasing bulbs, striped awning, glowing windows,
// buzzing neon, a hanging sign, pennants overhead. The portal
// remains the doorway; this is the building it belongs to.
// Coming-soon shops are boarded up and gray, neon reads SOON.
// ─────────────────────────────────────────────────────────────
// ⚠ Same origin contract as `GamePortalVisual` above, and it had the
// same bug: this drew at `store.x * tile` inside an anchor-relative 3D
// wrapper, which is why the TERROR TCG and SNAKE EYES CASINO marquees
// floated over open water, detached from any building.
// ⚑ `x`/`y` null = the 2D pier, where the origin is the map itself.
// `x`/`y` given = an anchor, and the facade centres on it, because
// `Pier3D` anchors a storefront at `store.x + store.w / 2`.
export function StorefrontFacade({ portal, tile, x = null, y = null }) {
  const store = portal.store;
  // the 2D fossil draws only north/south fronts; a side-on shop has no 2D art
  if (!store || store.face === "E" || store.face === "W") return null;
  const soon   = portal.status === "coming_soon";
  const color  = portal.color;
  const wPx    = store.w * tile;
  const marqH  = tile * 0.52;
  const awnH   = tile * 0.32;
  const wallH  = tile * 1.02;

  // marquee bulbs — chasing twinkle along the top edge
  const bulbCount = Math.max(10, Math.floor(wPx / 15));
  const bulbs = Array.from({ length: bulbCount }, (_, i) => (
    <div key={i} style={{
      position: "absolute",
      left: `${((i + 0.5) / bulbCount) * 100}%`, top: -3,
      width: 4.5, height: 4.5, borderRadius: "50%",
      transform: "translateX(-50%)",
      background: i % 2 === 0 ? color : SD.goldBright,
      boxShadow: `0 0 6px 1.5px ${i % 2 === 0 ? color : SD.goldBright}`,
      animation: soon ? "none" : `pierBulbTwinkle 1.5s ease-in-out ${(i * 0.11).toFixed(2)}s infinite alternate`,
      opacity: soon ? 0.3 : 1,
    }} />
  ));

  // pennant string above the marquee
  const penCount = Math.max(6, Math.floor(wPx / 26));
  const PEN_COLORS = [color, SD.goldBright, "#22d3ee"];
  const pennants = Array.from({ length: penCount }, (_, i) => (
    <div key={i} style={{
      width: 10, height: 12,
      clipPath: "polygon(0 0, 100% 0, 50% 100%)",
      background: PEN_COLORS[i % 3],
      opacity: soon ? 0.25 : 0.8,
      transformOrigin: "top center",
      animation: soon ? "none" : `pierPennantWave ${(1.6 + (i % 4) * 0.3).toFixed(1)}s ease-in-out ${(i * 0.17).toFixed(2)}s infinite alternate`,
      filter: `drop-shadow(0 0 3px ${PEN_COLORS[i % 3]}55)`,
    }} />
  ));

  // windows — glowing interiors flanking the doorway
  const winFracs = store.windows === 3 ? [0.07, 0.41, 0.76] : [0.09, 0.72];
  const windows = winFracs.map((f, i) => (
    <div key={i} style={{
      position: "absolute",
      left: `${f * 100}%`, bottom: tile * 0.10,
      width: `${store.windows === 3 ? 17 : 19}%`, height: wallH * 0.62,
      borderRadius: "40% 40% 4px 4px",
      border: `2px solid rgba(58,42,26,0.95)`,
      background: soon
        ? "repeating-linear-gradient(45deg, #241a10 0 6px, #191007 6px 12px)"
        : `radial-gradient(ellipse at 50% 65%, ${color}55 0%, ${color}22 55%, rgba(8,5,18,0.9) 100%)`,
      boxShadow: soon ? "none" : `0 0 ${10}px ${color}44, inset 0 0 12px ${color}33`,
      animation: soon ? "none" : `pierWindowGlow ${(2.4 + i * 0.7).toFixed(1)}s ease-in-out ${(i * 0.9).toFixed(1)}s infinite alternate`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: tile * 0.32, opacity: soon ? 0.8 : 1,
      overflow: "hidden",
    }}>
      {/* someone — or something — moving about inside */}
      {!soon && <span style={{ opacity: 0.35 }}>{portal.icon}</span>}
      {/* boarded up */}
      {soon && <div style={{
        position: "absolute", inset: 0,
        background: "linear-gradient(52deg, transparent 44%, #3a2a18 44%, #3a2a18 56%, transparent 56%), linear-gradient(-52deg, transparent 44%, #332414 44%, #332414 56%, transparent 56%)",
      }} />}
      {/* mullion */}
      {!soon && <div style={{
        position: "absolute", inset: 0,
        background: "linear-gradient(90deg, transparent 48%, rgba(58,42,26,0.9) 48%, rgba(58,42,26,0.9) 52%, transparent 52%), linear-gradient(0deg, transparent 48%, rgba(58,42,26,0.9) 48%, rgba(58,42,26,0.9) 52%, transparent 52%)",
      }} />}
    </div>
  ));

  return (
    <div style={{
      position: "absolute",
      left: x == null ? store.x * tile : x * tile + tile / 2 - wPx / 2,
      top:  y == null
        ? (store.face === "N" ? store.y * tile : store.y * tile - (marqH + awnH + wallH + 6))
        : y * tile,
      width: wPx, zIndex: 2, pointerEvents: "none",
      filter: soon ? "grayscale(0.22) brightness(0.88)" : "none",
    }}>
      {/* pennant string */}
      <div style={{
        position: "absolute", left: "2%", right: "2%", top: -14,
        display: "flex", justifyContent: "space-between",
        borderTop: "1px solid rgba(200,200,220,0.18)",   // the string itself
        paddingTop: 1,
      }}>{pennants}</div>

      {/* marquee board */}
      <div style={{
        position: "relative",
        height: marqH,
        background: `linear-gradient(180deg, rgba(24,15,40,0.97), rgba(10,6,22,0.97))`,
        border: `1.5px solid ${color}66`,
        borderBottom: "none",
        borderRadius: "6px 6px 0 0",
        boxShadow: `0 0 14px ${color}2e, inset 0 1px 0 ${color}33`,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {bulbs}
        <span style={{
          fontSize: Math.max(9, tile * 0.19), fontWeight: 900,
          letterSpacing: Math.max(2, tile * 0.09),
          fontFamily: SD.fontDisplay, color: soon ? SD.starDim : color,
          textShadow: soon ? "none" : `0 0 10px ${color}, 0 0 26px ${color}55`,
          whiteSpace: "nowrap",
        }}>{portal.label.toUpperCase()}</span>
      </div>

      {/* striped awning with scalloped hem */}
      <div style={{
        height: awnH,
        background: `repeating-linear-gradient(90deg, ${color}${soon ? "30" : "55"} 0 14px, rgba(240,230,208,0.14) 14px 28px)`,
        borderLeft: `1.5px solid ${color}44`,
        borderRight: `1.5px solid ${color}44`,
        boxShadow: "inset 0 -4px 8px rgba(0,0,0,0.4)",
        transformOrigin: "top center",
        animation: soon ? "none" : "pierAwningSway 4.5s ease-in-out infinite alternate",
      }} />
      <div style={{
        height: 7, marginTop: -1,
        background: `radial-gradient(circle at 8px -1px, ${color}${soon ? "30" : "50"} 6.5px, transparent 7.5px)`,
        backgroundSize: "16px 8px", backgroundRepeat: "repeat-x",
        transformOrigin: "top center",
        animation: soon ? "none" : "pierAwningSway 4.5s ease-in-out infinite alternate",
      }} />

      {/* shopfront wall */}
      <div style={{
        position: "relative",
        height: wallH, marginTop: -1,
        background: `linear-gradient(180deg, rgba(38,26,16,0.96) 0%, rgba(26,17,10,0.96) 100%), repeating-linear-gradient(0deg, transparent 0 7px, rgba(0,0,0,0.16) 7px 8px)`,
        border: `1.5px solid rgba(58,42,26,0.9)`,
        borderTop: "none",
        borderRadius: "0 0 4px 4px",
        boxShadow: `inset 0 6px 12px rgba(0,0,0,0.5), 0 4px 10px rgba(0,0,0,0.4)`,
      }}>
        {windows}
        {/* neon — OPEN hums, SOON sulks */}
        <div style={{
          position: "absolute", left: "50%", top: tile * 0.07,
          transform: "translateX(-50%)",
          fontSize: Math.max(7, tile * 0.135), fontWeight: 700,
          letterSpacing: 4, fontFamily: SD.fontDisplay,
          color: soon ? "#f87171" : color,
          textShadow: soon
            ? "0 0 6px #f8717188"
            : `0 0 5px ${color}, 0 0 12px ${color}, 0 0 26px ${color}88`,
          border: `1px solid ${soon ? "#f8717155" : color + "77"}`,
          borderRadius: 5, padding: "1px 8px",
          animation: `pierNeon ${soon ? 9 : 6}s linear infinite`,
          whiteSpace: "nowrap",
        }}>{soon ? "SOON" : "OPEN"}</div>
        {/* hanging shingle sign */}
        <div style={{
          position: "absolute", left: "31%", top: -2,
          transformOrigin: "top center",
          animation: soon ? "none" : "pierSignSway 3.2s ease-in-out infinite alternate",
        }}>
          <div style={{ width: 1.5, height: 7, background: "rgba(200,200,220,0.35)", margin: "0 auto" }} />
          <div style={{
            width: tile * 0.42, height: tile * 0.42,
            background: "rgba(14,9,26,0.95)",
            border: `1.5px solid ${color}88`, borderRadius: 5,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: tile * 0.24,
            boxShadow: `0 0 8px ${color}33`,
            filter: soon ? "grayscale(1)" : "none",
          }}>{portal.icon}</div>
        </div>
      </div>
    </div>
  );
}


// ⭐ THE WORLD, READABLE FROM OUTSIDE THIS FILE. The dev preview route
// (`?preview=pier`, development builds only — see index.js) mounts the
// 3D pier with the real map and needs the same data the live screen
// hands it. A hoisted FUNCTION, not a const, for the same module-cycle
// reason as every other export here.
export function getPierWorld() {
  return {
    map: MAP, cols: COLS, rows: ROWS,
    portals: GAME_PORTALS,
    interactables: INTERACTABLES,
    lampposts: LAMPPOSTS,
    spawn: SPAWN,
    hallFront: HALL_FRONT,
    solids: SOLIDS,
    props: PIER_PROPS,
    fronts: FRONTS,
    gate: GATE,
    squares: SQUARES,
    spine: SPINE,
    alleys: ALLEYS,
  };
}

export function Lamppost({ tile, x, y }) {
  const postH = tile * 0.95;
  return (
    <div style={{
      position: "absolute",
      left: x * tile, top: y * tile - postH,
      width: 0, zIndex: 4, pointerEvents: "none",
    }}>
      {/* light pool on the planks */}
      <div style={{
        position: "absolute", left: -tile * 0.55, top: postH - tile * 0.22,
        width: tile * 1.1, height: tile * 0.5, borderRadius: "50%",
        background: "radial-gradient(ellipse, rgba(255,196,102,0.20) 0%, transparent 70%)",
        filter: "blur(2px)",
        animation: "pierLampFlicker 6s ease-in-out infinite",
      }} />
      {/* iron post */}
      <div style={{
        position: "absolute", left: -1.5, top: 6,
        width: 3, height: postH - 6,
        background: "linear-gradient(90deg, #1c1626, #3a3350 45%, #171224)",
        borderRadius: 2,
      }} />
      {/* base */}
      <div style={{
        position: "absolute", left: -4, top: postH - 4,
        width: 8, height: 4, borderRadius: "40% 40% 20% 20%",
        background: "#241d36",
      }} />
      {/* curled arm */}
      <div style={{
        position: "absolute", left: -1, top: 3,
        width: 9, height: 8,
        borderTop: "2.5px solid #3a3350",
        borderRight: "2.5px solid #3a3350",
        borderRadius: "0 8px 0 0",
      }} />
      {/* lantern head */}
      <div style={{
        position: "absolute", left: 4, top: 8,
        width: 9, height: 11, borderRadius: "3px 3px 45% 45%",
        border: "1.5px solid #3a3350",
        background: "radial-gradient(circle at 50% 45%, #ffd98a 0%, #ffb84d 55%, rgba(120,70,20,0.5) 100%)",
        boxShadow: "0 0 12px 3px rgba(255,184,77,0.55)",
        animation: "pierLampFlicker 6s ease-in-out infinite",
      }} />
      <div style={{
        position: "absolute", left: 7.5, top: 5.5,
        width: 3, height: 3, borderRadius: "50%", background: "#3a3350",
      }} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// THE GATE SIGN — rising out of the Khao sea by the entrance.
// The grand welcome. And, faintly, beneath the fresh paint,
// the name W'ily would very much like everyone to forget.
// ─────────────────────────────────────────────────────────────
function PierGateSign({ tile }) {
  const bulbs = Array.from({ length: 16 }, (_, i) => {
    const onTop = i < 8;
    const f = ((i % 8) + 0.5) / 8;
    return (
      <div key={i} style={{
        position: "absolute",
        left: `${f * 100}%`, [onTop ? "top" : "bottom"]: -3,
        width: 5, height: 5, borderRadius: "50%",
        transform: "translateX(-50%)",
        background: i % 3 === 0 ? "#22d3ee" : SD.goldBright,
        boxShadow: `0 0 7px 2px ${i % 3 === 0 ? "#22d3ee" : SD.goldBright}`,
        animation: `pierBulbTwinkle 1.7s ease-in-out ${(i * 0.13).toFixed(2)}s infinite alternate`,
      }} />
    );
  });
  return (
    <div style={{
      position: "absolute",
      left: tile * 1.1, top: tile * 13.7,
      width: tile * 6.4, zIndex: 3, pointerEvents: "none",
    }}>
      {/* the board */}
      <div style={{
        position: "relative",
        padding: `${tile * 0.16}px ${tile * 0.2}px`,
        background: "linear-gradient(180deg, rgba(22,14,38,0.97), rgba(9,5,20,0.97))",
        border: `2px solid ${SD.gold}aa`,
        outline: `1px solid ${SD.gold}33`,
        outlineOffset: 3,
        borderRadius: 10,
        boxShadow: `0 0 24px ${SD.gold}33, inset 0 1px 0 ${SD.gold}44`,
        textAlign: "center", fontFamily: SD.fontDisplay,
      }}>
        {bulbs}
        <div style={{
          fontSize: Math.max(6, tile * 0.11), letterSpacing: 3,
          color: SD.starSilver, marginBottom: 2,
        }}>♥ WELCOME TO ♥</div>
        <div style={{
          fontSize: Math.max(11, tile * 0.30), fontWeight: 900,
          letterSpacing: Math.max(2, tile * 0.07), color: SD.goldBright,
          animation: "pierWordmarkGlow 4s ease-in-out infinite alternate",
        }}>W'ILY'S PIER</div>
        <div style={{
          fontSize: Math.max(5, tile * 0.085), letterSpacing: 2,
          color: "rgba(154,138,122,0.5)",
          textDecoration: "line-through",
          marginTop: 2, fontStyle: "italic",
        }}>THE UNSTABLE PIERRE</div>
      </div>
      {/* pilings sinking into the sea */}
      {[0.16, 0.84].map((f, i) => (
        <div key={i} style={{
          position: "absolute", left: `${f * 100}%`, top: "96%",
          width: 5, height: tile * 1.3, transform: "translateX(-50%)",
          background: "linear-gradient(180deg, rgba(58,42,26,0.95), transparent)",
          borderRadius: 2,
        }} />
      ))}
      {/* reflection shimmer in the Khao */}
      <div style={{
        position: "absolute", left: "8%", right: "8%", top: `calc(100% + ${tile * 1.1}px)`,
        height: tile * 0.5,
        background: `linear-gradient(180deg, ${SD.gold}22, transparent)`,
        filter: "blur(5px)", opacity: 0.7,
        transform: "scaleY(-1)",
      }} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// THE MYSTERY — something stands at the east end now.
// A dark monolith. It does not advertise. It waits.
// ─────────────────────────────────────────────────────────────
export function MysteryVisual({ tile, x, y }) {
  const violet = "#6d5aa8";
  return (
    <div style={{
      position: "absolute",
      left: x * tile - tile * 0.5, top: (y - 1.6) * tile,
      width: tile, height: tile * 2, zIndex: 4, pointerEvents: "none",
      display: "flex", alignItems: "flex-end", justifyContent: "center",
    }}>
      {/* ground shadow */}
      <div style={{
        position: "absolute", bottom: -tile * 0.08,
        width: tile * 0.9, height: tile * 0.3, borderRadius: "50%",
        background: "radial-gradient(ellipse, rgba(0,0,0,0.55) 0%, transparent 70%)",
      }} />
      {/* the monolith */}
      <div style={{
        width: tile * 0.34, height: tile * 1.15,
        background: "linear-gradient(180deg, #0c0918 0%, #060410 100%)",
        border: `1px solid ${violet}44`,
        borderRadius: "3px 3px 1px 1px",
        clipPath: "polygon(12% 0, 88% 0, 100% 100%, 0 100%)",
        boxShadow: `0 0 14px rgba(109,90,168,0.22), inset 0 0 10px rgba(0,0,0,0.9)`,
      }} />
      {/* orbiting wisps — the only light it permits */}
      {[0, 1].map(i => (
        <div key={i} style={{
          position: "absolute", left: "50%", top: "42%",
          width: 3, height: 3, marginLeft: -1.5, marginTop: -1.5,
          borderRadius: "50%",
          background: violet,
          boxShadow: `0 0 6px ${violet}`,
          animation: `pierWispOrbit ${8 + i * 5}s linear ${i * -4}s infinite`,
          ["--wispR"]: `${tile * (0.32 + i * 0.14)}px`,
        }} />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PIER ATMOSPHERE — fireflies over the planks, void-gulls
// crossing the sky. Pure CSS motion, zero animation frames.
// ─────────────────────────────────────────────────────────────
function PierAtmosphere({ tile }) {
  const [flies] = useState(() =>
    Array.from({ length: 14 }, (_, i) => ({
      id: i,
      left: 0.04 + Math.random() * 0.92,          // fraction of map width
      top: 0.12 + Math.random() * 0.78,           // fraction of map height
      dur: 5 + Math.random() * 6,
      delay: Math.random() * 7,
      fx: (Math.random() * 2 - 1) * 46,
      fy: -(8 + Math.random() * 30),
      c: Math.random() < 0.72 ? SD.goldBright : "#22d3ee",
      r: 2 + Math.random() * 1.6,
    }))
  );
  const W = 56 * tile, H = 20 * tile;
  const gulls = [
    { top: H * 0.095, dur: 36, delay: 0,  scale: 1 },
    { top: H * 0.925, dur: 52, delay: 14, scale: 0.8 },
  ];
  return (
    <div style={{ position: "absolute", inset: 0, zIndex: 5, pointerEvents: "none" }}>
      {flies.map(f => (
        <div key={f.id} style={{
          position: "absolute",
          left: f.left * W, top: f.top * H,
          width: f.r, height: f.r, borderRadius: "50%",
          background: f.c,
          boxShadow: `0 0 ${f.r * 2.5}px ${f.c}`,
          animation: `pierFirefly ${f.dur.toFixed(1)}s ease-in-out ${f.delay.toFixed(1)}s infinite`,
          ["--fx"]: `${f.fx.toFixed(0)}px`,
          ["--fy"]: `${f.fy.toFixed(0)}px`,
        }} />
      ))}
      {gulls.map((g, i) => (
        <div key={`gull-${i}`} style={{
          position: "absolute", left: 0, top: g.top,
          animation: `pierGullX ${g.dur}s linear ${g.delay}s infinite`,
          ["--gullSpan"]: `${W + 160}px`,
        }}>
          <div style={{
            animation: "pierGullY 2.3s ease-in-out infinite alternate",
            transform: `scale(${g.scale})`,
          }}>
            <svg width="22" height="10" viewBox="0 0 22 10">
              <path d="M1 8 Q6 1 11 6 Q16 1 21 8"
                stroke="rgba(212,201,184,0.55)" strokeWidth="1.6"
                fill="none" strokeLinecap="round" />
            </svg>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// GAME PORTAL MODAL — party-aware since Phase 3.
// Leader in a party of 2+ → "launch together" starts the ready-up.
// Anyone can still go alone; the party persists (?partyId= rides
// along), because a party is a social contract, not a session.
// ─────────────────────────────────────────────────────────────
function GamePortalModal({ portal, profile, playerUid, partyId, party, isLeader, onPartyLaunch, onOpenQuests, onClose }) {
  const crests = profile?.divineCrests || 0;
  const memberCount = Object.keys(party?.members || {}).length;
  const canPartyLaunch = portal.status === "live" && !!portal.url &&
    !!partyId && isLeader && memberCount > 1;

  const launchSolo = () => {
    if (portal.status !== "live" || !portal.url) return;
    window.location.href = withPartyParam(portal.url, playerUid, partyId);
  };
  const launchParty = () => {
    if (!canPartyLaunch) return;
    onPartyLaunch(portal);
    onClose();
  };

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 200,
      background: "radial-gradient(ellipse at 50% 40%, rgba(15,8,40,0.82) 0%, rgba(0,0,0,0.9) 75%)",
      backdropFilter: "blur(2px)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: `linear-gradient(165deg, rgba(12,7,34,0.98) 0%, rgba(5,3,20,0.98) 55%, ${portal.color}0d 100%)`,
        border: `1px solid ${portal.color}88`,
        borderRadius: 16, padding: "32px 40px",
        width: "min(420px, 90vw)", textAlign: "center",
        boxShadow: `0 0 40px ${portal.color}33, inset 0 1px 0 ${portal.color}33`,
      }}>
        <div style={{ fontSize: 56, marginBottom: 12 }}>{portal.icon}</div>
        <div style={{
          fontSize: 18, fontFamily: SD.fontDisplay, fontWeight: 900,
          color: portal.color, letterSpacing: 3,
          textShadow: `0 0 16px ${portal.color}`, marginBottom: 6,
        }}>{portal.label.toUpperCase()}</div>
        <div style={{
          fontSize: 11, color: SD.starDim, fontStyle: "italic",
          fontFamily: SD.fontDisplay, marginBottom: 20, lineHeight: 1.6,
        }}>{portal.flavor}</div>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          background: "rgba(255,255,255,0.04)", borderRadius: 8, padding: "8px 16px",
          marginBottom: 20, border: `1px solid ${SD.void4}`,
          fontFamily: SD.fontDisplay,
        }}>
          <span style={{ fontSize: 14 }}>◆</span>
          <span style={{ fontSize: 13, color: SD.gold, fontWeight: 900 }}>{crests.toLocaleString()}</span>
          <span style={{ fontSize: 9, color: SD.starDim, letterSpacing: 2 }}>DIVINE CRESTS</span>
        </div>
        {portal.status === "coming_soon" ? (
          <div style={{
            padding: "12px", borderRadius: 8,
            border: `1px solid ${SD.void4}`,
            color: SD.starDim, fontSize: 11, fontFamily: SD.fontDisplay,
            letterSpacing: 2, marginBottom: 16,
          }}>COMING SOON</div>
        ) : (
          <>
            {canPartyLaunch && (
              <button onClick={launchParty} style={{
                width: "100%", padding: "14px",
                background: `linear-gradient(135deg, ${portal.color}44, ${portal.color}66)`,
                border: `1px solid ${portal.color}`,
                borderRadius: 8, cursor: "pointer",
                color: portal.color, fontSize: 13,
                fontFamily: SD.fontDisplay, fontWeight: 900,
                letterSpacing: 3,
                boxShadow: `0 0 24px ${portal.color}55`,
                marginBottom: 10,
              }}>♥ {portal.verb.toUpperCase()} TOGETHER ({memberCount})</button>
            )}
            <button onClick={launchSolo} style={{
              width: "100%", padding: canPartyLaunch ? "11px" : "14px",
              background: canPartyLaunch
                ? "transparent"
                : `linear-gradient(135deg, ${portal.color}33, ${portal.color}55)`,
              border: `1px solid ${portal.color}${canPartyLaunch ? "66" : ""}`,
              borderRadius: 8, cursor: "pointer",
              color: portal.color, fontSize: canPartyLaunch ? 11 : 13,
              fontFamily: SD.fontDisplay, fontWeight: 900,
              letterSpacing: 3,
              boxShadow: canPartyLaunch ? "none" : `0 0 20px ${portal.color}44`,
              marginBottom: 12,
            }}>◆ {canPartyLaunch ? "GO ALONE" : portal.verb.toUpperCase()}</button>
            {!!partyId && !isLeader && memberCount > 1 && (
              <div style={{
                fontSize: 8, color: SD.starDim, letterSpacing: 1,
                fontFamily: SD.fontDisplay, marginBottom: 12, lineHeight: 1.6,
              }}>YOUR PARTY RIDES WITH YOU — THE LEADER CAN LAUNCH EVERYONE TOGETHER</div>
            )}
          </>
        )}
        {/* ⭐ THE REPRESENTATIVE. Somebody from that game is standing at
            its door with work. They pay in ✹ Dawgs, out of the Pier's
            own pocket — the other title is only ever READ, so no
            partner repo changes by one line. */}
        {onOpenQuests && (
          <button onClick={() => onOpenQuests(portal)} style={{
            width: "100%", padding: "10px",
            background: "transparent", border: "1px solid #9ad46a55",
            borderRadius: 8, cursor: "pointer",
            color: "#9ad46a", fontSize: 10,
            fontFamily: SD.fontDisplay, letterSpacing: 2, marginBottom: 10,
          }}>✹ SPEAK TO THE REPRESENTATIVE</button>
        )}
        <button onClick={onClose} style={{
          width: "100%", padding: "10px",
          background: "transparent", border: `1px solid ${SD.void4}`,
          borderRadius: 8, cursor: "pointer",
          color: SD.starDim, fontSize: 10,
          fontFamily: SD.fontDisplay, letterSpacing: 2,
        }}>← BACK TO THE PIER</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DIALOGUE PANEL — now a typewriter.
// ─────────────────────────────────────────────────────────────
// Types each line character-by-character (the 🟢 DialogueEngine upgrade,
// wired for W'ily). Click / Space / Enter skips the typing to full line, then
// advances; Esc closes. Keeps the exact call signature and visual language it
// always had — { lines, color, speaker, onClose } — so no caller changes.
// ─────────────────────────────────────────────────────────────
function DialoguePanel({ lines, color, speaker, onClose }) {
  const [idx, setIdx]       = useState(0);
  const [typed, setTyped]   = useState("");
  const [typing, setTyping] = useState(true);

  const current = lines[idx] || "";
  const atEnd   = idx >= lines.length - 1 && !typing;

  // Typewriter — retype whenever the line changes. Ellipsis-only lines ("…")
  // linger a touch longer per char so the pauses in his speech land.
  useEffect(() => {
    setTyped(""); setTyping(true);
    let i = 0;
    const isPause = /^[.… ]+$/.test(current);
    const speed = isPause ? 90 : 26;
    const t = setInterval(() => {
      i++;
      setTyped(current.slice(0, i));
      if (i >= current.length) { clearInterval(t); setTyping(false); }
    }, speed);
    return () => clearInterval(t);
  }, [idx, current]);

  const advance = () => {
    if (typing) { setTyped(current); setTyping(false); }   // skip to full line
    else if (idx < lines.length - 1) setIdx(i => i + 1);   // next line
    else onClose();                                         // done
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Enter" || e.key === " " || e.key === "q" || e.key === "Q") {
        e.preventDefault(); advance();
      }
      if (e.key === "Escape" || e.key === "Backspace") {
        e.preventDefault(); onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div onClick={advance} style={{
      position: "fixed", bottom: 80, left: "50%", transform: "translateX(-50%)",
      width: "min(560px, 92vw)",
      background: `linear-gradient(170deg, rgba(11,7,32,0.97) 0%, rgba(5,3,20,0.97) 60%, ${color}0a 100%)`,
      border: `1px solid ${color}88`,
      borderRadius: 12, padding: "18px 22px",
      zIndex: 200, cursor: "pointer",
      boxShadow: `0 0 28px ${color}33, inset 0 1px 0 ${color}2e`,
    }}>
      <div style={{ fontSize: 9, color, letterSpacing: 3,
        fontFamily: SD.fontDisplay, marginBottom: 8 }}>
        {speaker.toUpperCase()}
      </div>
      <div style={{ fontSize: 14, color: SD.starWhite,
        fontFamily: SD.fontDisplay, lineHeight: 1.7, minHeight: 24 }}>
        {typed}
        {typing && <span style={{ opacity: 0.45, color }}>▌</span>}
      </div>
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginTop: 10,
      }}>
        {/* line-dot progress */}
        <div style={{ display: "flex", gap: 4 }}>
          {lines.map((_, i) => (
            <div key={i} style={{
              width: i === idx ? 14 : 5, height: 5, borderRadius: 3,
              transition: "width 0.2s",
              background: i === idx ? color : `${color}44`,
            }} />
          ))}
        </div>
        <div style={{ fontSize: 8, color: SD.starDim, letterSpacing: 1 }}>
          {typing ? "▶ SKIP" : (atEnd ? "▶ CLOSE" : "▶ CONTINUE")}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// CREST HUD
// ─────────────────────────────────────────────────────────────
// Registry-driven. Divine Crests always show — they're the Empire's
// reserve and the Pier is their home. Every other registered currency
// appears only when the player is actually carrying some, so the HUD
// stays quiet for a new player and grows as they travel the Empire.
// Adding a currency to the Pier's HUD is a row in CURRENCIES, not an
// edit here.
// The same chips CrestHUD draws, as rows of data for the menu backdrop's header.
function crestChips(wallet) {
  const hudMeta = (id) => CURRENCIES[id] || (id === UD_ID ? UD_ROW : null);
  return [...Object.keys(CURRENCIES), UD_ID]
    .filter((id) => id === DC_ID || (wallet?.[id] || 0) > 0)
    .map((id) => ({
      id, icon: hudMeta(id).icon, label: hudMeta(id).short,
      value: (wallet?.[id] || 0).toLocaleString(),
      tint: id === UD_ID ? UD_ROW.tint : tintFor(id),
    }));
}

function CrestHUD({ wallet }) {
  const hudMeta = (id) => CURRENCIES[id] || (id === UD_ID ? UD_ROW : null);
  const shown = [...Object.keys(CURRENCIES), UD_ID].filter(
    (id) => id === DC_ID || (wallet?.[id] || 0) > 0
  );

  return (
    <div style={{
      position: "fixed", top: 14, right: 14, zIndex: 150,
      display: "flex", alignItems: "center", gap: 10,
      fontFamily: SD.fontDisplay, flexWrap: "wrap",
      // ⚠ `60vw` IS 216px ON A 360px PHONE, and one chip is ~95-115px —
      // so the two currencies a new player already carries wrapped onto
      // two rows, and four wrapped onto three, pushing the stack down
      // into the party HUD at top:58 and the invite flash at top:110.
      // The constraint is the LOG OUT button on the left, not a
      // fraction of the screen.
      justifyContent: "flex-end", maxWidth: "calc(100vw - 130px)",
    }}>
      {shown.map((id) => {
        const meta = hudMeta(id);
        const tint = id === UD_ID ? UD_ROW.tint : tintFor(id);
        return (
          <div key={id} style={{
            // ⚑ `uiChip` WAS LIFTED OUT OF THIS PILL and then shipped
            // with no call sites — the helper and the thing it was copied
            // from sat side by side, differing only in a radius. It is
            // the same chip now, and the currency gradient rides on top
            // of it, because a tinted ground per currency is the one part
            // of this that is genuinely about the money and not the
            // chrome.
            ...uiChip(tint),
            background: `linear-gradient(150deg, rgba(14,10,32,0.95), rgba(5,3,20,0.92) 70%, ${tint}12)`,
            border: `1px solid ${tint}`,
          }}>
            <span style={{ fontSize: 13, color: tint }}>{meta.icon}</span>
            <span style={{ fontSize: 13, fontWeight: 900, color: tint }}>
              {(wallet?.[id] || 0).toLocaleString()}
            </span>
            <span style={{ fontSize: 8, color: UI.dim, letterSpacing: UI.track.micro }}>
              {meta.short}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN — W'ILY'S PIER
// ─────────────────────────────────────────────────────────────
export default function UnstablePierre({ playerUid, playerName, profile, onLogout }) {
  const [tile, setTile]     = useState(calcTile);
  const [pos, setPos]       = useState(SPAWN);
  const [facing, setFacing] = useState({ x: 0, y: 1 });
  const [camOffset, setCamOffset] = useState({ x: 0, y: 0 });
  const [otherPlayers, setOtherPlayers] = useState({});
  const [liveProfile, setLiveProfile]   = useState(profile);
  // Every registered currency, live. One listener per currency under
  // the hood; adding a title's coin to the Pier is a CURRENCIES row.
  const [wallet, setWallet]             = useState({});

  const [nearPortal, setNearPortal]           = useState(null);
  const [nearNPC, setNearNPC]                 = useState(null);
  const [showPortalModal, setShowPortalModal] = useState(null);
  const [dialogue, setDialogue]               = useState(null);
  const [showExchange, setShowExchange]       = useState(false);
  const [showBazaar, setShowBazaar]           = useState(false);
  const [showMarket, setShowMarket]           = useState(false);
  // ⭐ ONE SKY, WORN EVERYWHERE (caP'n, 2026-09-25): the sky you wear over
  // your Minerium is the sky over the pier. Nothing written = Open Unstable
  // Sky. One tiny field listener; it changes when you change sky, never per
  // frame, so it cannot touch the movement loop.
  const [wornSkyId, setWornSkyId]             = useState(null);

  // ── HOME (instanced trophy wing) ─────────────────────────
  // When set to { ownerUid, ownerName }, PierHome takes over the screen.
  // ownerUid === playerUid → your own hall; otherwise you're visiting.
  const [homeSession, setHomeSession] = useState(null);
  // ⭐ THE MINERIUM. Set alongside homeSession rather than instead of it,
  // so stepping back inside from the yard is one setState and lands you
  // in the Hall you came from rather than back out on the boardwalk.
  const [mineriumSession, setMineriumSession] = useState(null);
  // Which portal's representative you are talking to, if any.
  const [questPortal, setQuestPortal] = useState(null);
  // A player you're standing next to (for the visit / party pill).
  const [nearPlayer, setNearPlayer]   = useState(null);

  useEffect(() => {
    if (!playerUid) return undefined;
    const off = onValue(ref(db, `homes/${playerUid}/minerium/sky`), (snap) => {
      const id = snap.val();
      setWornSkyId(MINERIUM_ITEMS[id]?.kind === MINERIUM_KIND.SKY ? id : null);
    }, () => setWornSkyId(null));
    return () => off();
  }, [playerUid]);

  // ── THE MARK ─────────────────────────────────────────────
  // Click any nameplate — yours or theirs — and you get their ID at a
  // glance. Read-only, any uid, no plumbing: the Mark reads the shared
  // profile directly. Editing is a separate owner-gated surface.
  const [markUid, setMarkUid]   = useState(null);
  const [editMark, setEditMark] = useState(false);
  // Her question sheet is a menu like any other: WASD pauses, pills step aside.
  const guideOpen = useAemiliaOpen();
  // The menu backdrop is a menu like any other too.
  const backdropOpen = useBackdropOpen();
  const inBackdrop = useBackdropActive();
  const bandView = useBand();

  useEffect(() => { ensureKeyframes(); }, []);

  const [pulseFrame, setPulseFrame] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setPulseFrame(f => f + 1), 80);
    return () => clearInterval(t);
  }, []);
  const districtPulse = 0.5 + 0.5 * Math.sin(pulseFrame * 0.04);

  const keysRef = useRef({});
  const moveRef = useRef(null);
  // ⚠ posRef is now the SOURCE OF TRUTH for position, and `pos` state
  // is a throttled MIRROR of it. That inversion is the whole reason the
  // 3D view is smooth: movement flows through refs into useFrame and
  // React hears about it a few times a second instead of every step.
  // (ie-realtime-perf Cause 1: MOVEMENT NEVER RE-RENDERS.)
  const posRef  = useRef(pos);
  const facingRef = useRef(facing);

  // ── ⭐ THE AGENT SEAT's legs ────────────────────────────────────────
  // Refs only, so the loop (rebuilt on every modal change) can never
  // hold a stale copy. agentSteer() hands the loop a WORLD-space unit
  // vector — not fake keys, because keys are camera-relative (pierCamera)
  // and her camera may be pointing anywhere.
  const agentRef = useRef({ path: null, i: 0, follow: null, status: "idle", repathAt: 0, goalAt: null, prog: null, tries: 0 });
  const agentPlan = useCallback((to, why) => {
    const a = agentRef.current;
    const r = findPath(posRef.current, to);
    if (!r.ok) { a.path = null; a.status = `can't: ${r.reason}`; return r; }
    a.path = r.points; a.i = 0; a.goalAt = { x: to.x, y: to.y }; a.prog = null;
    a.status = why || "walking";
    return r;
  }, []);
  const agentSteer = useCallback((p, ts) => {
    const a = agentRef.current;
    if (a.follow) {
      const o = otherPlayersRef.current[a.follow];
      if (!o) { a.follow = null; a.path = null; a.status = "lost them"; return { x: 0, y: 0 }; }
      if (Math.hypot(o.x - p.x, o.y - p.y) < 1.6) { a.path = null; a.status = "with them"; return { x: 0, y: 0 }; }
      const moved = !a.goalAt || Math.hypot(o.x - a.goalAt.x, o.y - a.goalAt.y) > 1.2;
      if ((!a.path || moved) && ts > a.repathAt) { a.repathAt = ts + 1200; agentPlan(o, "following"); }
    }
    if (!a.path) return { x: 0, y: 0 };
    let wp = a.path[a.i];
    while (wp && Math.hypot(wp.x - p.x, wp.y - p.y) < 0.35) { a.i++; a.prog = null; a.tries = 0; wp = a.path[a.i]; }
    if (!wp) { a.path = null; a.status = a.follow ? "with them" : "arrived"; return { x: 0, y: 0 }; }
    // STUCK: no real progress toward this waypoint for 2.5 s → re-plan
    // from where she stands; twice in a row and she gives up and says so.
    const d = Math.hypot(wp.x - p.x, wp.y - p.y);
    if (!a.prog || d < a.prog.d - 0.3) a.prog = { d, ts };
    else if (ts - a.prog.ts > 2500) {
      a.tries += 1;
      if (a.tries > 2 || !a.goalAt) { a.path = null; a.follow = null; a.tries = 0; a.status = "stuck"; return { x: 0, y: 0 }; }
      agentPlan(a.goalAt, a.status);
      return { x: 0, y: 0 };
    }
    return { x: (wp.x - p.x) / d, y: (wp.y - p.y) / d };
  }, [agentPlan]);

  // ── ⭐ THE MINERIUM'S BOOT ────────────────────────────────
  // Two jobs, once per session.
  //
  // 1 · REGISTER ✹ UP DAWGS. `ensureCurrency` seeds the rate row if the
  //     node is empty and is inert forever after, so a rate caP'n has
  //     tuned in the Firebase console is never un-tuned by somebody
  //     opening the app. Once the row exists the HUD, the wallet, the
  //     Well's target picker and the Bazaar's price picker all show
  //     Dawgs with no further code anywhere — that is L16, the registry
  //     seam, doing exactly what it was built for.
  //
  // 2 · RUN THE CONTENT AUDITS. Every content file this Empire has
  //     shipped eventually grew a row pointing at nothing, and the
  //     cheapest moment to find that is the first second of a session
  //     rather than the moment a player clicks it. These are console
  //     warnings only and can never break a boot.
  useEffect(() => {
    ensureDawgsRegistered();
    const problems = [
      ...auditCatalogue(),
      ...auditPropArt(),
      ...auditCompanions(),
    ];
    if (problems.length) {
      // eslint-disable-next-line no-console
      console.warn(`[MINERIUM] ${problems.length} content problem(s):\n  ` + problems.join("\n  "));
    }
  }, []);

  // ⭐ THE PIER IS 3D. caP'n, 2026-08-25: "yes default and no toggle."
  //
  // Not a mode, not a preference, not an experiment — THE pier. There
  // is no switch and no persisted choice, because a toggle would have
  // said "we are not sure yet," and we are.
  //
  // The 2D renderer is still in this file and still reads the same
  // world, unreferenced by the running app. That is a DELIBERATE
  // fossil, and this comment is the header the purge gate looks for:
  // ⚠ NOT DEAD CODE. It is the reference the 3D view is checked
  // against, and it is one boolean from being live if the 3D pier ever
  // has to be stood down in a hurry. Do not delete it on "nothing
  // renders it."
  const is3D = true;
  // ⚠ A yaw left over from the 3D rig would silently rotate input in
  // the 2D view, and nothing would report it. Reset on unmount, always.
  useEffect(() => () => resetGroundForward(), []);
  const otherPlayersRef = useRef(otherPlayers);
  otherPlayersRef.current = otherPlayers;

  const gender      = liveProfile?.gender || "f";
  // ⭐ THE SAME COMPANION AS TERROR (pierFolk3D.resolveCompanionId): the profile's
  // equippedCompanion, else the starter deck's; the pier's old field only as a last resort.
  const companionId = resolveCompanionId(liveProfile) || liveProfile?.companionId || "default";

  const { walkFrame, isIdle, idleFrame } = usePlayerAnimation(pos);

  useEffect(() => {
    const onResize = () => setTile(calcTile());
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  useEffect(() => {
    setCamOffset(calcCamOffset(pos.x, pos.y, tile, COLS, ROWS));
  }, [pos, tile]);

  useEffect(() => {
    if (!playerUid) return;
    return listenToProfile(playerUid, p => { if (p) setLiveProfile(p); });
  }, [playerUid]);

  useEffect(() => {
    if (!playerUid) return;
    return listenToWallet(playerUid, setWallet);
  }, [playerUid]);

  // ── THE PARTY — persistent, cross-game, cap 4 ─────────────
  // This replaced the lightweight homeInvites/{uid} flow along the
  // exact seam left for it. PierHome.js untouched — it only ever
  // cared about ownerUid.
  const partySys = usePartySystem({
    uid: playerUid,
    playerName,
    currentGame: "pier",
  });

  useEffect(() => {
    if (!playerUid || !playerName) return;
    // ⚠ AND YOU ARE NOT ON THE PIER WHEN YOU ARE INSIDE. The early
    // return below swaps the subtree without unmounting this effect, so
    // without this guard your boardwalk presence kept heartbeating from
    // inside your own house. `PierHome` runs its own instanced presence
    // at `homes/{ownerUid}/players/`; this one's cleanup removes the
    // pier row on the way in and re-creates it on the way out.
    if (homeSession || mineriumSession) return;
    const myRef = ref(db, `games/pierre/players/${playerUid}`);
    set(myRef, {
      // ⚠ where you ARE, not the spawn: coming back out of your Hall wrote
      // (8,10) while you stood at the Hall door, and the other players saw
      // you jump to the entrance until you next moved (third review)
      name: playerName, x: posRef.current.x, y: posRef.current.y,
      facingX: 0, facingY: 1, gender, companionId, ts: Date.now(),
    });
    const unsub = onValue(ref(db, "games/pierre/players"), snap => {
      const all = snap.val() || {};
      const cutoff = Date.now() - 15000;
      const others = {};
      Object.entries(all).forEach(([otherUid, data]) => {
        if (otherUid === playerUid) return;
        if (!data.ts || data.ts < cutoff) {
          remove(ref(db, `games/pierre/players/${otherUid}`));
          return;
        }
        others[otherUid] = data;
      });
      const capped = {};
      Object.entries(others).slice(0, 4).forEach(([u, d]) => { capped[u] = d; });
      setOtherPlayers(capped);
    });
    const heartbeat = setInterval(() => {
      set(ref(db, `games/pierre/players/${playerUid}/ts`), Date.now());
    }, 5000);
    return () => {
      unsub(); clearInterval(heartbeat); remove(myRef);
    };
  }, [playerUid, playerName, gender, companionId, homeSession, mineriumSession]);

  const lastSync = useRef(0);
  const syncPos = useCallback((x, y, fx, fy) => {
    if (!playerUid) return;
    const now = Date.now();
    if (now - lastSync.current < 80) return;
    lastSync.current = now;
    update(ref(db, `games/pierre/players/${playerUid}`), {
      name: playerName, x, y, facingX: fx, facingY: fy,
      gender, companionId, ts: now,
    });
  }, [playerUid, playerName, gender, companionId]);

  // ── Zone chat — "pier" scope ─────────────────────────────
  const chat = useChatSystem({
    zoneId: "pier",
    uid: playerUid,
    playerName,
    pos,
  });

  // ── ⭐ THE SPEECH BUBBLE's source (2026-09-26, Pier3D SpeechBubble) ──
  // Everyone's latest pier line, keyed by uid, in a REF: the 3D bubbles
  // read it every frame, so a new chat line re-renders nothing in the
  // scene. `at` is when THIS client first saw the line (local clock).
  const speechRef = useRef({});
  const speechSeen = useRef(new Map());          // message id → first seen (ms)
  useEffect(() => {
    const seenAt = speechSeen.current, next = {};
    const live = new Set();
    for (const m of chat.messages || []) {
      if (!m || !m.uid || !m.id) continue;
      live.add(m.id);
      if (typeof m.text !== "string" || !m.text.trim()) continue;   // only a raw writer sends these
      if (!seenAt.has(m.id)) seenAt.set(m.id, Date.now());
      const at = seenAt.get(m.id), prev = next[m.uid];
      if (!prev || at > prev.at || (at === prev.at && (m.ts || 0) >= (prev.ts || 0))) {
        next[m.uid] = { id: m.id, text: String(m.text || ""), at, ts: m.ts || 0 };
      }
    }
    for (const id of [...seenAt.keys()]) if (!live.has(id)) seenAt.delete(id);   // stays as small as the feed
    speechRef.current = next;
  }, [chat.messages]);

  useEffect(() => {
    const onDown = e => { keysRef.current[e.key] = true; };
    const onUp   = e => { keysRef.current[e.key] = false; };
    // ⚠ mutate, never replace — see the orphaned-ref law in the loop.
    const clear  = () => { const k = keysRef.current; for (const kk in k) k[kk] = false; };
    window.addEventListener("keydown", onDown);
    window.addEventListener("keyup",   onUp);
    window.addEventListener("blur",    clear);
    return () => {
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("keyup",   onUp);
      window.removeEventListener("blur",    clear);
    };
  }, []);

  // ── THE SOCIAL LAYER'S SUPPRESSION SIGNAL ────────────────────────
  // IEFriends is mounted in index.js as a sibling of <App/>, which is
  // what makes it one line per title — and means it cannot see this
  // screen's modal state. Without this it floats its pill over the
  // ReadyUpModal's blackout during the 15-second party launch, over
  // the portal modal, and over an open Mark. Same predicate as the
  // movement lock below, on purpose: if the Pier is not taking WASD,
  // it is not taking a friends pill either.
  useEffect(() => {
    setSocialHidden(
      !!showPortalModal || !!dialogue || showExchange || showBazaar || showMarket ||
      // party chat docked in the band is part of the band, not a modal over it
      chat.open || (partySys.chatOpen && !bandView.active) || !!partySys.readyup ||
      !!markUid || editMark || guideOpen
    );
  }, [showPortalModal, dialogue, showExchange, showBazaar, showMarket, chat.open,
      partySys.chatOpen, partySys.readyup, markUid, editMark, guideOpen, bandView.active]);
  useEffect(() => () => setSocialHidden(false), []);

  useEffect(() => {
    // ⚠⚠ `homeSession` / `mineriumSession` BELONG IN THIS LIST, and their
    // absence was not a modal oversight — it is the shape of the screen
    // swap below. `if (homeSession) return <PierHome/>` is an EARLY
    // RETURN INSIDE THIS COMPONENT: it replaces the rendered subtree but
    // unmounts none of THIS component's own hooks. So while you walked
    // your Hall, the pier's key listener and this integrator were still
    // live, and every W/A/S/D that moved you indoors ALSO walked your
    // avatar across the public boardwalk — at 12.5 writes a second to
    // `games/pierre/players/{uid}`, on top of the Hall's own presence.
    // Other players watched you drift across the pier while you were in
    // your own trophy room. Found by adversarial review 2026-09-13.
    const indoors = !!homeSession || !!mineriumSession;
    const menuOpen = indoors || !!showPortalModal || !!dialogue || showExchange ||
      showBazaar || showMarket || chat.open || partySys.chatOpen || !!partySys.readyup ||
      !!markUid || editMark || guideOpen || backdropOpen;
    // ⚠ CLEAR BY MUTATION, NEVER BY REPLACEMENT. The key listeners and
    // this loop share one object; replacing it orphans whichever side
    // captured the old one, and it breaks in total silence — no error,
    // no warning, no console line. (ie-realtime-perf Cause 3b.)
    const clearKeys = () => { const k = keysRef.current; for (const kk in k) k[kk] = false; };
    if (menuOpen) { clearKeys(); return; }
    // The seat's stuck-timer counts rAF time; a menu pause is not "stuck".
    if (AGENT_SEAT) agentRef.current.prog = null;

    // ── THE ANALOG INTEGRATOR ────────────────────────────────────────
    // Was: one whole tile every 150ms. That is 6.67 tiles/second, and
    // SPEED_TPS is derived from exactly that number so the FEEL is
    // unchanged and only the granularity moves. Behind a follow camera,
    // hopping a whole tile at a time reads as broken; this is the same
    // walk, sampled continuously.
    const SPEED_TPS = 1000 / 150;
    const STATE_HZ  = 15;    // how often React hears about it
    const SYNC_HZ   = 8;     // how often the network hears about it
    const NEAR_HZ   = 7;     // how often proximity is re-tested
    let last = 0, tState = 0, tSync = 0, tNear = 0;
    let syncedX = null, syncedY = null;

    // Walkability at a continuous position. The player's CENTRE decides,
    // same as it always did — the pier has no tight corridors, so a
    // radius test would only cost feel without buying anything.
    const walkAt = (x, y) => isWalkable(Math.round(x), Math.round(y)) && !blockedBySolid(x, y);

    // Eight-way facing for the sprite. Kept COARSE on purpose: the
    // sprites are 2D and octant-gated, so continuous heading changes
    // cannot storm React with facing updates.
    const octant = (dx, dy) => (Math.abs(dx) > Math.abs(dy)
      ? { x: dx > 0 ? 1 : -1, y: 0 }
      : { x: 0, y: dy > 0 ? 1 : -1 });

    function loop(ts) {
      if (!last) last = ts;
      const dt = Math.min(0.05, (ts - last) / 1000);   // clamp a tab-switch
      last = ts;

      const k = keysRef.current;
      let ix = 0, iy = 0;
      if (k["ArrowLeft"]  || k["a"] || k["A"]) ix = -1;
      if (k["ArrowRight"] || k["d"] || k["D"]) ix =  1;
      if (k["ArrowUp"]    || k["w"] || k["W"]) iy = -1;
      if (k["ArrowDown"]  || k["s"] || k["S"]) iy =  1;

      // ⭐ CAMERA-RELATIVE. W means "away from the camera", always. In
      // the 2D view the shared forward is still its identity (0,-1), so
      // this line is a no-op there — which is the only reason it is safe
      // to apply unconditionally.
      let dir = rotateInput(ix, iy);
      // ⭐ AGENT SEAT: a held key is a human taking over — the seat's
      // route is dropped on the spot. No key held → the seat steers.
      if (AGENT_SEAT) {
        if (dir.x || dir.y) {
          const a = agentRef.current;
          a.humanAt = Date.now();     // look() reports this, so the brain backs off even when she was idle
          if (a.path || a.follow) { a.path = null; a.follow = null; a.status = "taken over"; }
        } else {
          dir = agentSteer(posRef.current, ts);
        }
      }

      if (dir.x || dir.y) {
        const p  = posRef.current;
        const st = SPEED_TPS * dt;
        let nx = p.x + dir.x * st;
        let ny = p.y + dir.y * st;
        // WALL SLIDE — a blocked diagonal keeps the free axis instead of
        // dead-stopping you against a railing.
        if (!walkAt(nx, ny)) {
          if (walkAt(nx, p.y))      ny = p.y;
          else if (walkAt(p.x, ny)) nx = p.x;
          else { nx = p.x; ny = p.y; }
        }
        posRef.current = { x: nx, y: ny };

        const f = octant(dir.x, dir.y);
        if (f.x !== facingRef.current.x || f.y !== facingRef.current.y) {
          facingRef.current = f;
          setFacing(f);
        }
      }

      // ── the three throttles. React, the network and proximity all
      // run far slower than the walk, and none of them can tell. ──
      const P = posRef.current;
      if (ts - tState > 1000 / STATE_HZ) {
        tState = ts;
        setPos(prev => (prev.x === P.x && prev.y === P.y ? prev : P));
      }
      // ⚠ ONLY WRITE WHEN IT MOVED. The old loop only reached this
      // line on a step; a timer alone would put eight presence writes a
      // second on the wire for a player standing perfectly still.
      if (ts - tSync > 1000 / SYNC_HZ && (P.x !== syncedX || P.y !== syncedY)) {
        tSync = ts; syncedX = P.x; syncedY = P.y;
        syncPos(P.x, P.y, facingRef.current.x, facingRef.current.y);
      }
      if (ts - tNear > 1000 / NEAR_HZ) {
        tNear = ts;
        const pr = getNearby(P, GAME_PORTALS);
        const n  = getNearby(P, INTERACTABLES);
        setNearPortal(pr || null);
        setNearNPC((!pr && n) ? n : null);
        const np = (!pr && !n)
          ? getNearbyPlayer(P, otherPlayersRef.current, 2)
          : null;
        setNearPlayer(np);
      }

      moveRef.current = requestAnimationFrame(loop);
    }
    moveRef.current = requestAnimationFrame(loop);
    return () => { if (moveRef.current) cancelAnimationFrame(moveRef.current); };
  // ⚠⚠ homeSession AND mineriumSession ARE IN THIS LIST NOW. The comment at
  // the top of this effect has said since 2026-09-13 that they belong here,
  // and the list never had them — so entering your Hall changed nothing the
  // effect depended on, the loop never stopped, and W/A/S/D inside the Hall
  // ALSO walked your pier avatar and wrote it back to presence, re-creating
  // the row the presence cleanup had just removed. Other players watched a
  // ghost of you stroll the boardwalk. (Third adversarial review, CONFIRMED.)
  }, [showPortalModal, dialogue, showExchange, showBazaar, showMarket, chat.open,
      partySys.chatOpen, partySys.readyup, markUid, editMark, syncPos,
      homeSession, mineriumSession, agentSteer, guideOpen, backdropOpen]);   // agentSteer is stable (refs only) — listed for the linter, never re-runs this

  const handleNPCInteract = (npc) => {
    if (npc.locked) {
      setDialogue({ lines: ["…", "…", "Not yet."], color: "#475569", speaker: "???" });
      return;
    }
    // ⭐ A RECRUIT (pierPlan RECRUITS): its pitch, then — if its game is live — the
    // game's own door (the portal screen: play, quests, challenges). A coming-soon
    // wing's recruit just talks: a placeholder, per caP'n ("if they have no function
    // leave as is").
    if (npc.recruit) {
      const portal = GAME_PORTALS.find((p) => p.id === npc.recruit.wing);
      const pitch = (RECRUITS[npc.recruit.wing] || [])[4] || "…";
      const live = portal && portal.status === "live";
      setDialogue({
        lines: live ? [pitch] : [pitch, `${portal ? portal.label : "This wing"} isn't open yet. I'll be right here when it is.`],
        color: portal ? portal.color : "#e8d9ff", speaker: npc.label,
        then: live ? () => setShowPortalModal(portal) : null,
      });
      return;
    }
    if (npc.id === "wily") {
      setDialogue({ lines: getWilyDialogue(liveProfile), color: SD.purpleLight, speaker: "W'ily" });
    } else if (npc.id === "crest_well") {
      setShowExchange(true);
    } else if (npc.id === "old_sign") {
      setDialogue({ lines: getOldSignDialogue(), color: "#8b7355", speaker: "Weathered Sign" });
    } else if (npc.id === "bazaar") {
      setShowBazaar(true);
    } else if (npc.id === "marketplace") {
      setShowMarket(true);
    } else if (npc.id === "home") {
      enterOwnHome();
    }
  };

  // ── Home entry / visiting ─────────────────────────────────
  // Enter your OWN hall (ownerUid = you).
  const enterOwnHome = useCallback(() => {
    setHomeSession({ ownerUid: playerUid, ownerName: playerName });
  }, [playerUid, playerName]);

  // Visit another player's hall directly (ownerUid = them).
  const visitHome = useCallback((ownerUid, ownerName) => {
    if (!ownerUid) return;
    setHomeSession({ ownerUid, ownerName: ownerName || "A wanderer" });
  }, []);

  // ── ⭐ AEMILIA: what her "Take me there" opens from the boardwalk ──
  // Indoors the pier's own modals don't render (the Hall/Minerium is an
  // early return below), so there she hides and offers no doors at all.
  // `showMarket` carries WHICH counter to open ("marks" | "minerium"); every
  // other read of it is truthiness, which a string keeps.
  const indoorsNow = !!homeSession || !!mineriumSession;
  useEffect(() => { setAemiliaHidden(indoorsNow, "indoors"); }, [indoorsNow]);
  useEffect(() => () => setAemiliaHidden(false, "indoors"), []);
  useAemiliaPlaces(indoorsNow ? {} : {
    "name-change": () => {
      setShowMarket("marks");
      setTimeout(() => {
        const el = document.getElementById("ie-name-change");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 400);
    },
    "skies": () => setShowMarket("minerium"),
    "my-mark": () => setEditMark(true),
    "crest-well": () => setShowExchange(true),
    "bazaar": () => setShowBazaar(true),
    "my-hall": () => enterOwnHome(),
  });

  // ── ⭐ THE MENU BACKDROP'S ROWS (OW-46) ──────────────────────────
  // caP'n: "allll buttons need to go in that backdrop. Except of course buttons
  // the player really needs to function during peak combat/gameplay". On the
  // boardwalk that leaves the talk/enter pill, the D-pad and the party card
  // (it only exists while you're in a party) on screen; everything below moved
  // in. Indoors the Hall/Minerium keep their own chrome, so the backdrop hides.
  // layout effect: the flag lands before paint, so neither camera bar flashes at a door
  useLayoutEffect(() => { setBackdropHidden(indoorsNow, "indoors"); }, [indoorsNow]);
  useEffect(() => () => setBackdropHidden(false, "indoors"), []);
  const [, camTick] = useState(0);
  useEffect(() => subscribeCamera(() => camTick((v) => v + 1)), []);
  const camView = getView(), camPosed = isPosed();
  useBackdropItems(indoorsNow ? [] : [
    ...(is3D ? [{
      // "free" = you orbited it yourself (the old bar's FREE tag); DEFAULT snaps back
      id: "camera", section: "play", label: camPosed ? "Camera · free" : "Camera", icon: "◉", order: 10,
      choices: [
        ...VIEW_ORDER.map((id) => ({ id, label: VIEWS[id].label, active: camView === id && !camPosed, run: () => chooseView(id) })),
        { id: "default", label: "DEFAULT", active: false, run: () => resetCamera() },
      ],
    }] : []),
    { id: "logout", section: "session", label: "Log out", icon: "⊗ ", run: () => onLogout && onLogout() },
  ]);
  useBackdropChips(indoorsNow ? [] : crestChips(wallet));
  // The party card is a band panel on a computer (only while you're in a party).
  // World chat registers its own tab (ChatSystem `railed`), Friends its own (IEFriends).
  const partyInBand = bandView.active && !!partySys.partyId;
  const partyShown = partyInBand && !bandView.collapsed && bandView.panel === "party" && !bandView.quiet;
  // party chat closes with its panel, or it would hold WASD with nothing on screen
  useEffect(() => { if (partyInBand && !partyShown && partySys.chatOpen) partySys.setChatOpen(false); },
    [partyInBand, partyShown, partySys]);
  useBackdropTabs(!indoorsNow && partySys.partyId ? [{ id: "party", label: "Party", icon: "♢", order: 30 }] : []);
  const bandReserve = useBandReserve();

  // ── Party invite from the proximity pill ──────────────────
  const [inviteFlash, setInviteFlash] = useState(null); // brief feedback text
  const partyUp = useCallback(async (targetUid, targetName) => {
    const res = await partySys.invitePlayer(targetUid, targetName);
    if (res?.ok) setInviteFlash(`Invite sent to ${targetName || "them"} ♥`);
    else if (res?.reason === "party_full") setInviteFlash("Your party is full (4/4).");
    else setInviteFlash("The invite slipped into the void. Try again.");
    setTimeout(() => setInviteFlash(null), 2500);
  }, [partySys]);

  // ── ⭐ THE AGENT SEAT's eyes and hands — window.__ieAgent ─────────
  // Everything the API reads is mirrored into one ref every render, so
  // the API object is made ONCE and never holds a stale closure.
  // (`handleNPCInteract` is a per-render function; the arrow below
  // captures this render's copy and is only ever CALLED, never read at
  // render time — so declaring it above this block is not required.)
  const agentSeeRef = useRef(null);
  agentSeeRef.current = {
    nearPortal, nearNPC, nearPlayer, dialogue, messages: chat.messages,
    busy: homeSession ? "in the Hall" : mineriumSession ? "in the Minerium"
      : dialogue ? "reading a dialogue" : chat.open ? "chat box open"
      : showPortalModal ? "a door menu is open" : showExchange ? "the Crest Well is open"
      : showBazaar ? "the Bazaar is open" : showMarket ? "the shop is open"
      : (markUid || editMark) ? "a Mark is open" : (partySys.chatOpen || partySys.readyup) ? "party screen open"
      : guideOpen ? "Aemilia's questions are open"
      : backdropOpen ? "the menu is open"
      : null,
    talk: (npc) => handleNPCInteract(npc),
    closeDialog: () => setDialogue(null),      // never runs `then`: a recruit's then opens a door menu
    say: chat.say,
  };
  useEffect(() => {
    if (!AGENT_SEAT) return undefined;
    const see = () => agentSeeRef.current;
    const me = () => ({ uid: playerUid, name: playerName, x: +posRef.current.x.toFixed(2), y: +posRef.current.y.toFixed(2) });
    const places = () => [
      ...GAME_PORTALS.filter((p) => p.x >= 0).map((p) => ({ id: p.id, label: `${p.label} door`, kind: "door", open: p.status === "live", x: p.x, y: p.y })),
      ...INTERACTABLES.map((o) => ({ id: o.id, label: o.label, kind: o.recruit ? "recruit" : "landmark", locked: !!o.locked, x: o.x, y: o.y })),
      { id: "spawn", label: "the gate (spawn)", kind: "landmark", x: SPAWN.x, y: SPAWN.y },
    ];
    const findPlayer = (who) => {
      const all = otherPlayersRef.current || {};
      if (all[who]) return [who, all[who]];
      const low = String(who || "").toLowerCase();
      return Object.entries(all).find(([, o]) => String(o.name || "").toLowerCase() === low) || null;
    };
    let lastSay = 0, lastGoTo = 0;
    const api = {
      version: 1,
      places,
      look() {
        const s = see(), p = posRef.current, a = agentRef.current;
        const others = Object.entries(otherPlayersRef.current || {}).map(([uid, o]) => ({
          uid, name: o.name, x: +(+o.x).toFixed(2), y: +(+o.y).toFixed(2),
          dist: +Math.hypot(o.x - p.x, o.y - p.y).toFixed(1),
        })).sort((m, n) => m.dist - n.dist);
        return {
          t: Date.now(), me: me(),
          walking: !!a.path, status: a.status,
          msSinceHuman: a.humanAt ? Date.now() - a.humanAt : null,   // a human's keys in HER window
          following: a.follow ? (otherPlayersRef.current[a.follow]?.name || a.follow) : null,
          near: {
            door: s.nearPortal ? { id: s.nearPortal.id, label: s.nearPortal.label } : null,
            npc: s.nearNPC ? { id: s.nearNPC.id, label: s.nearNPC.label, verb: s.nearNPC.verb } : null,
            player: s.nearPlayer ? { uid: s.nearPlayer.targetUid, name: s.nearPlayer.targetName } : null,
          },
          others,
          chat: (s.messages || []).slice(-12).map((m) => ({
            id: m.id, uid: m.uid, name: m.name, text: m.text, ts: m.ts, mine: m.uid === playerUid,
            dist: +Math.hypot((m.x || 0) - p.x, (m.y || 0) - p.y).toFixed(1),
          })),
          dialogue: s.dialogue ? { speaker: s.dialogue.speaker, lines: s.dialogue.lines } : null,
          busy: s.busy,
        };
      },
      goTo(target) {
        const a = agentRef.current;
        let to = null, label = null;
        const now = Date.now();
        if (now - lastGoTo < 400) return { ok: false, reason: "slow down — one route at a time" };
        lastGoTo = now;
        const num = (v) => v !== null && v !== "" && Number.isFinite(Number(v));
        if (target && typeof target === "object" && num(target.x) && num(target.y)) { to = { x: +target.x, y: +target.y }; label = `${to.x},${to.y}`; }
        else {
          const pl = places().find((q) => q.id === target);
          if (pl) { to = pl; label = pl.label; }
          else { const f = findPlayer(target); if (f) { to = { x: f[1].x, y: f[1].y }; label = f[1].name; } }
        }
        if (!to) return { ok: false, reason: `don't know "${target}" — try places() or a player's name` };
        a.follow = null; a.tries = 0;
        const r = agentPlan(to, `walking to ${label}`);
        return r.ok ? { ok: true, to: label, tiles: +r.length.toFixed(1) } : r;
      },
      follow(who) {
        const f = findPlayer(who);
        if (!f) return { ok: false, reason: `nobody called "${who}" is on the pier` };
        const a = agentRef.current;
        a.follow = f[0]; a.path = null; a.goalAt = null; a.repathAt = 0; a.tries = 0; a.status = "following";
        return { ok: true, following: f[1].name };
      },
      stop() { const a = agentRef.current; a.path = null; a.follow = null; a.status = "idle"; return { ok: true }; },
      async say(text) {
        const s = see();
        // A program can talk faster than anyone can read. One line per
        // 1.5 s, so a brain stuck in a loop can never flood the pier.
        const now = Date.now();
        if (now - lastSay < 1500) return { ok: false, reason: "slow down — one line at a time" };
        lastSay = now;
        if (!s.say) return { ok: false, reason: "chat isn't ready" };
        const sent = await s.say(text, posRef.current);
        return sent ? { ok: true } : { ok: false, reason: "nothing to say, or not signed in" };
      },
      interact() {
        const s = see();
        if (s.dialogue) return { ok: false, reason: "a dialogue is already open — read it with look(), then closeDialog()" };
        const npc = s.nearNPC;
        if (!npc) return { ok: false, reason: s.nearPortal ? "doors aren't open to you yet" : "nobody close enough — walk up first" };
        const talky = npc.locked || npc.recruit || npc.id === "wily" || npc.id === "old_sign";
        if (!talky) return { ok: false, reason: `${npc.label} isn't open to you yet` };
        s.talk(npc);
        return { ok: true, with: npc.label };
      },
      closeDialog() { see().closeDialog(); return { ok: true }; },
    };
    window.__ieAgent = api;
    // eslint-disable-next-line no-console
    console.info("[AGENT SEAT] window.__ieAgent is live for", playerName || playerUid);
    return () => { if (window.__ieAgent === api) delete window.__ieAgent; };
  // playerUid/playerName are fixed for this screen's life; agentPlan is stable.
  }, [playerUid, playerName, agentPlan]);

  const allPlayers = [
    {
      uid: playerUid, isLocal: true,
      data: { x: pos.x, y: pos.y, facingX: facing.x, facingY: facing.y,
        gender, companionId, name: playerName },
    },
    ...Object.entries(otherPlayers).map(([uid, d]) => ({
      uid, isLocal: false, data: d,
    })),
  ];

  // ⚠ MEMOISED, and that is load-bearing. Pier3D is React.memo'd; a
  // fresh object here every render would make the memo do nothing and
  // reconcile the entire 3D tree on every HUD tick.
  const world3D = useMemo(() => ({
    map: MAP, cols: COLS, rows: ROWS,
    portals: GAME_PORTALS,
    interactables: INTERACTABLES,
    lampposts: LAMPPOSTS,
    spawn: SPAWN,
    hallFront: HALL_FRONT,
    solids: SOLIDS,
    props: PIER_PROPS,
    fronts: FRONTS,
    gate: GATE,
    squares: SQUARES,
    spine: SPINE,
    alleys: ALLEYS,
  }), []);

  // ⚠ THE ROSTER KEY, NOT THE ROSTER. Presence ticks arrive several
  // times a second per moving player; handing that object to a memo'd
  // 3D scene would reconcile it at exactly the rate everyone is
  // walking. This string changes only when somebody arrives or leaves,
  // and WHERE they are travels by ref instead.
  const rosterKey = useMemo(
    () => Object.keys(otherPlayers).sort().join(","),
    [otherPlayers]
  );

  const wilyNpc      = INTERACTABLES.find(n => n.id === "wily");
  const crestWellNpc = INTERACTABLES.find(n => n.id === "crest_well");
  const oldSignNpc   = INTERACTABLES.find(n => n.id === "old_sign");
  const bazaarNpc    = INTERACTABLES.find(n => n.id === "bazaar");
  const marketNpc    = INTERACTABLES.find(n => n.id === "marketplace");
  const homeNpc      = INTERACTABLES.find(n => n.id === "home");
  const mysteryNpc   = INTERACTABLES.find(n => n.id === "mystery");

  const isPartyMember = (uid) =>
    !!partySys.party && !!partySys.party.members && !!partySys.party.members[uid];

  // ── If we're in a hall, PierHome takes over the whole screen ──
  // ⚠⚠ AMENDED 2026-09-13 — THIS COMMENT USED TO BE FALSE, and a false
  // comment about lifecycle is worse than none. It claimed "the pier's
  // presence effect unmounts here (this component's subtree is
  // replaced)". An early return does NOT unmount the returning
  // component's own hooks — only its children. Every effect in
  // UnstablePierre stayed live behind the Hall. Presence and the
  // movement loop are now explicitly gated on `homeSession ||
  // mineriumSession` (see both, above); anything ELSE added to this
  // component from here on is live while a player is indoors unless it
  // is gated the same way.
  // ── ⭐ THE MINERIUM takes the whole screen, ahead of the Hall ──
  // Checked FIRST, so stepping outside is a mount and stepping back in
  // is an unmount, with the Hall still underneath either way.
  if (mineriumSession) {
    return (
      <MineriumScreen
        ownerUid={mineriumSession.ownerUid}
        ownerName={mineriumSession.ownerName}
        viewerUid={playerUid}
        viewerName={playerName}
        profile={liveProfile}
        onEnterHall={() => setMineriumSession(null)}
        onLeave={() => { setMineriumSession(null); setHomeSession(null); }}
      />
    );
  }

  if (homeSession) {
    return (
      <PierHome
        ownerUid={homeSession.ownerUid}
        ownerName={homeSession.ownerName}
        viewerUid={playerUid}
        viewerName={playerName}
        profile={liveProfile}
        onEnterMinerium={() => setMineriumSession({
          ownerUid: homeSession.ownerUid, ownerName: homeSession.ownerName,
        })}
        onLeave={() => setHomeSession(null)}
      />
    );
  }

  return (
    <div style={{
      position: "fixed", inset: 0, overflow: "hidden",
      background: "#020010", fontFamily: SD.fontDisplay,
    }}>
      {/* ── THE 3D PIER ────────────────────────────────────────────
          A RENDERER SWAP, and nothing else. Same map, same refs, same
          Firebase, same HUD, same modals — the game does not notice
          which one is mounted. */}
      {is3D && (
        <Pier3D
          world={world3D}
          posRef={posRef}
          facingRef={facingRef}
          gender={gender}
          companionId={companionId}
          playerName={playerName}
          othersRef={otherPlayersRef}
          rosterKey={rosterKey}
          skyId={wornSkyId}
          playerUid={playerUid}
          speechRef={speechRef}
        />
      )}

      {!is3D && (
      <div style={{
        position: "absolute",
        left: -camOffset.x, top: -camOffset.y,
        width: COLS * tile, height: ROWS * tile,
      }}>
        <UnstableWorld tile={tile} />
        <PierPlanks tile={tile} pulse={districtPulse} />

        {/* The boardwalk itself — gate sign, storefronts, lampposts */}
        <PierGateSign tile={tile} />
        {GAME_PORTALS.map(p => (
          <StorefrontFacade key={`store-${p.id}`} portal={p} tile={tile} />
        ))}
        {LAMPPOSTS.map((l, i) => (
          <Lamppost key={`lamp-${i}`} tile={tile} x={l.x} y={l.y} />
        ))}

        {crestWellNpc && <CrestWellVisual tile={tile} x={crestWellNpc.x} y={crestWellNpc.y} />}
        {wilyNpc && <WilySparkles tile={tile} x={wilyNpc.x} y={wilyNpc.y} />}
        {oldSignNpc && <OldSignVisual tile={tile} x={oldSignNpc.x} y={oldSignNpc.y} />}
        {bazaarNpc && <BazaarStallVisual tile={tile} x={bazaarNpc.x} y={bazaarNpc.y} />}
        {marketNpc && <MysticAllyStall tile={tile} x={marketNpc.x} y={marketNpc.y} />}
        {homeNpc && <HomeEntranceVisual tile={tile} x={homeNpc.x} y={homeNpc.y} />}
        {mysteryNpc && <MysteryVisual tile={tile} x={mysteryNpc.x} y={mysteryNpc.y} />}

        {GAME_PORTALS.map(p => <GamePortalVisual key={p.id} portal={p} tile={tile} />)}

        {/* Fireflies + void-gulls — the pier breathes */}
        <PierAtmosphere tile={tile} />

        {INTERACTABLES.filter(n => !n.locked && n.id !== "old_sign").map(n => (
          <div key={n.id} style={{
            position: "absolute",
            left: n.x * tile - 20,
            top: n.y * tile - 16,
            fontSize: 8, color: n.color,
            fontFamily: SD.fontDisplay, letterSpacing: 2,
            textShadow: `0 0 6px ${n.color}`,
            zIndex: 6, whiteSpace: "nowrap", pointerEvents: "none",
          }}>{n.label.toUpperCase()}</div>
        ))}

        {allPlayers.map(({ uid, isLocal, data }) => (
          <PlayerEntity
            key={uid}
            pos={{ x: data.x, y: data.y }}
            facing={{ x: data.facingX ?? 0, y: data.facingY ?? 1 }}
            gender={data.gender || "f"}
            walkFrame={isLocal ? walkFrame : false}
            isIdle={isLocal ? isIdle : false}
            idleFrame={isLocal ? idleFrame : false}
            tile={tile}
            companionId={data.companionId || "default"}
            showCompanion={isLocal}
          />
        ))}

        {allPlayers.map(({ uid, isLocal, data }) => {
          const inMyParty = !isLocal && isPartyMember(uid);
          return (
            <div
              key={`name-${uid}`}
              onClick={() => setMarkUid(uid)}
              title={isLocal ? "Your Mark" : `${data.name || "Their"} Mark`}
              style={{
                position: "absolute",
                left: data.x * tile + tile / 2,
                top:  data.y * tile - tile * 0.6,
                transform: "translateX(-50%)",
                whiteSpace: "nowrap", fontSize: 9,
                color: isLocal ? SD.goldBright : (inMyParty ? SD.purpleLight : SD.starSilver),
                background: "rgba(0,0,0,0.75)",
                padding: "2px 7px", borderRadius: 4,
                border: `1px solid ${
                  isLocal ? SD.gold + "88" : (inMyParty ? SD.purpleLight + "aa" : SD.void4)
                }`,
                boxShadow: inMyParty ? `0 0 8px ${SD.purpleLight}44` : "none",
                fontFamily: SD.fontDisplay,
                zIndex: 11,
                pointerEvents: "auto",
                cursor: "pointer",
              }}
            >{inMyParty ? "♥ " : ""}{data.name || "???"} ◆</div>
          );
        })}

        {nearPortal && !showPortalModal && (
          <ProximityBubble
            color={nearPortal.color}
            verb={nearPortal.verb}
            label={nearPortal.label}
            tileX={nearPortal.x} tileY={nearPortal.y}
            tile={tile}
            portrait={isPortrait()}
            comingSoon={nearPortal.status === "coming_soon"}
            onActivate={() => setShowPortalModal(nearPortal)}
            onDismiss={() => {}}
          />
        )}
        {nearNPC && !nearPortal && !dialogue && !showExchange && !showBazaar && !showMarket && (
          <ProximityBubble
            color={nearNPC.color}
            verb={nearNPC.verb}
            label={nearNPC.label}
            tileX={nearNPC.x} tileY={nearNPC.y}
            tile={tile}
            portrait={isPortrait()}
            onActivate={() => handleNPCInteract(nearNPC)}
            onDismiss={() => {}}
          />
        )}

        {/* Nearby-player visit / party pill */}
        {nearPlayer && !nearPortal && !nearNPC && !dialogue && !showExchange && !showBazaar && !showMarket && (() => {
          const pd = otherPlayers[nearPlayer.targetUid];
          if (!pd) return null;
          const alreadyPartied = isPartyMember(nearPlayer.targetUid);
          return (
            <div style={{
              position: "absolute",
              left: pd.x * tile + tile / 2,
              top:  pd.y * tile - tile * 1.15,
              transform: "translateX(-50%)",
              display: "flex", gap: 6, zIndex: 60,
              animation: "bubbleFloat 1.8s ease-in-out infinite alternate",
            }}>
              <button onClick={() => setMarkUid(nearPlayer.targetUid)} style={{
                background: `linear-gradient(135deg, ${SD.starSilver}22, rgba(4,2,14,0.95))`,
                border: `1px solid ${SD.starSilver}88`, borderRadius: 8,
                padding: "5px 10px", cursor: "pointer",
                color: SD.starWhite, fontSize: 9, letterSpacing: 1,
                fontFamily: SD.fontDisplay, whiteSpace: "nowrap",
              }}>◆ MARK</button>
              <button onClick={() => visitHome(nearPlayer.targetUid, nearPlayer.targetName)} style={{
                background: `linear-gradient(135deg, ${SD.gold}22, rgba(4,2,14,0.95))`,
                border: `1px solid ${SD.gold}88`, borderRadius: 8,
                padding: "5px 10px", cursor: "pointer",
                color: SD.goldBright, fontSize: 9, letterSpacing: 1,
                fontFamily: SD.fontDisplay, whiteSpace: "nowrap",
              }}>⌂ VISIT HALL</button>
              {!alreadyPartied && (
                <button onClick={() => partyUp(nearPlayer.targetUid, nearPlayer.targetName)} style={{
                  background: `linear-gradient(135deg, ${SD.purpleLight}22, rgba(4,2,14,0.95))`,
                  border: `1px solid ${SD.purpleLight}88`, borderRadius: 8,
                  padding: "5px 10px", cursor: "pointer",
                  color: SD.purpleLight, fontSize: 9, letterSpacing: 1,
                  fontFamily: SD.fontDisplay, whiteSpace: "nowrap",
                }}>♥ PARTY UP</button>
              )}
            </div>
          );
        })()}
      </div>
      )}

      {/* ── THE 3D INTERACTION PILL ────────────────────────────────
          The 2D bubbles are positioned in TILE space, which means
          nothing once the camera can stand anywhere. In 3D the same
          proximity state drives one fixed pill instead — and it is in
          the same corner every time, so a missing one is obvious. */}
      {is3D && (nearPortal || nearNPC || nearPlayer) && !showPortalModal &&
        !dialogue && !showExchange && !showBazaar && !showMarket && (
        <div style={{
          // centred on what is left of the screen beside the band
          position: "fixed", left: `calc((100% - ${bandReserve}px) / 2)`, bottom: 96, transform: "translateX(-50%)",
          zIndex: 120, display: "flex", gap: 8, alignItems: "center",
          animation: "bubbleFloat 1.8s ease-in-out infinite alternate",
        }}>
          {nearPortal ? (
            <button onClick={() => setShowPortalModal(nearPortal)} style={pill3D(nearPortal.color)}>
              {nearPortal.verb.toUpperCase()} · {nearPortal.label}
              {nearPortal.status === "coming_soon" ? "  (SOON)" : ""}
            </button>
          ) : nearNPC ? (
            <button onClick={() => handleNPCInteract(nearNPC)} style={pill3D(nearNPC.color)}>
              {nearNPC.verb.toUpperCase()} · {nearNPC.label}
            </button>
          ) : (
            <>
              <button onClick={() => setMarkUid(nearPlayer.targetUid)} style={pill3D(SD.starSilver)}>◆ MARK</button>
              <button onClick={() => visitHome(nearPlayer.targetUid, nearPlayer.targetName)} style={pill3D(SD.gold)}>⌂ VISIT HALL</button>
              {!isPartyMember(nearPlayer.targetUid) && (
                <button onClick={() => partyUp(nearPlayer.targetUid, nearPlayer.targetName)} style={pill3D(SD.purpleLight)}>♥ PARTY UP</button>
              )}
            </>
          )}
        </div>
      )}

      {/* Cinematic vignette — sits above the world, below all chrome */}
      <div style={{
        position: "fixed", inset: 0, zIndex: 90, pointerEvents: "none",
        background: "radial-gradient(ellipse at 50% 44%, transparent 52%, rgba(2,0,16,0.32) 82%, rgba(2,0,16,0.62) 100%)",
      }} />

      {!inBackdrop && <CrestHUD wallet={wallet} />}

      {/* Party — roster HUD, chat, incoming invites */}
      {partyInBand
        ? (partyShown && (
            <BandPortal><PartyHUD {...partySys} dock={railPanelStyle(bandView.slotTop, bandView.hostBottom, 0)} /></BandPortal>
          ))
        : <PartyHUD {...partySys} top={58} left={14} />}
      <PartyInviteBanner {...partySys} />

      {/* Invite feedback flash */}
      {inviteFlash && (
        <div style={{
          position: "fixed", top: 110, left: "50%", transform: "translateX(-50%)",
          zIndex: 230, background: "rgba(5,3,20,0.96)",
          border: `1px solid ${SD.purpleLight}66`, borderRadius: 10,
          padding: "8px 16px", fontSize: 10, color: SD.starSilver,
          fontFamily: SD.fontDisplay, letterSpacing: 1,
          boxShadow: `0 0 16px ${SD.purpleLight}33`,
        }}>{inviteFlash}</div>
      )}

      {/* ⚠ SoloInvitePill retired 2026-08-21 — and note HOW it was retired,
          because the first attempt white-screened the Pier for every solo
          player. A JSX comment is only a comment in CHILDREN position;
          inside `{cond && ( … )}` it is an EXPRESSION, and `{/* … *\/}` is
          a legal EMPTY OBJECT LITERAL. So `!partyId && {}` evaluated to
          `{}` and React threw "Objects are not valid as a React child" on
          the most common path in the game. It parsed clean and ESLint said
          nothing. A comment replacing a conditional must replace the whole
          conditional.

          The Empire-wide FRIENDS pill (IEFriends.js, mounted in index.js)
          owns this corner now and does strictly more: it invites, it shows
          who is online, and it says which game they are standing in. Two
          "invite friends" buttons in the same 114x28 box was the single
          most visibly stitched-on thing in the build. PartySystem still
          exports SoloInvitePill if another screen ever wants it. */}

      {!inBackdrop && <div style={{
        position: "fixed", top: 14, left: 14, zIndex: 150,
        display: "flex", gap: 8, fontFamily: SD.fontDisplay,
      }}>
        <button onClick={onLogout} style={{
          background: "rgba(5,3,20,0.9)", border: `1px solid ${SD.void4}`,
          borderRadius: 8, padding: "6px 14px",
          color: SD.starDim, cursor: "pointer",
          fontSize: 10, letterSpacing: 2,
        }}>⊗ LOG OUT</button>
      </div>}

      {/* ⚠ THE PRODUCT'S OWN NAME WAS THE LEAST READABLE TEXT ON SCREEN:
          9px in `#6a5f9e` — a violet that exists in no palette — at
          3.5:1, on `zIndex: 100`, centred at `bottom: 14`, which on a
          phone is exactly where the camera bar sits at `zIndex: 500`.
          The wordmark was drawn UNDERNEATH the camera controls.
          Now: the house gold, bigger, above the bar, and lifted clear of
          it on a narrow screen. */}
      {!inBackdrop && <div style={{
        position: "fixed",
        bottom: isMobile() ? 62 : 16, left: "50%", transform: "translateX(-50%)",
        fontSize: 11, color: SD.gold, letterSpacing: ".5em", paddingLeft: ".5em",
        fontFamily: SD.fontDisplay, zIndex: 520, pointerEvents: "none",
        whiteSpace: "nowrap",
        textShadow: `0 0 18px ${SD.gold}55, 0 2px 10px #000`,
        animation: "pierWordmarkGlow 5s ease-in-out infinite alternate",
      }}>♥ W&apos;ILY&apos;S PIER ♥</div>}

      {isMobile() && <MobileDpad keysRef={keysRef} />}

      {/* Zone chat — floaties, panel, toggle */}
      <ChatSystem
        {...chat}
        // ⭐ In 3D the SPEECH BUBBLE shows every line over its speaker; the 2D
        // floaties (emoji-only, placed with 2D screen maths) are off there.
        floaties={is3D ? [] : chat.floaties}
        players={otherPlayers}
        tile={tile}
        camOffset={camOffset}
        railed
        localUid={playerUid}
      />

      {showPortalModal && (
        <GamePortalModal
          portal={showPortalModal}
          profile={liveProfile}
          playerUid={playerUid}
          partyId={partySys.partyId}
          party={partySys.party}
          isLeader={partySys.isLeader}
          onPartyLaunch={partySys.startReadyUp}
          onOpenQuests={(p) => { setQuestPortal(p); setShowPortalModal(null); }}
          onClose={() => setShowPortalModal(null)}
        />
      )}

      {/* ⭐ THE REPRESENTATIVE'S BOARD */}
      {questPortal && (
        <PierQuestBoard
          gameId={questPortal.id}
          uid={playerUid}
          onClose={() => setQuestPortal(null)}
        />
      )}

      {/* The synchronized launch — 15 seconds, ready-up to skip */}
      {partySys.readyup && (
        <ReadyUpModal
          readyup={partySys.readyup}
          party={partySys.party}
          uid={playerUid}
          setReady={partySys.setReady}
          cancelReadyUp={partySys.cancelReadyUp}
          isLeader={partySys.isLeader}
        />
      )}

      {dialogue && (
        <DialoguePanel
          lines={dialogue.lines}
          color={dialogue.color}
          speaker={dialogue.speaker}
          onClose={() => { const next = dialogue.then; setDialogue(null); if (next) next(); }}
        />
      )}

      {showExchange && (
        <CrestExchange
          uid={playerUid}
          profile={liveProfile}
          wallet={wallet}
          onClose={() => setShowExchange(false)}
        />
      )}

      {showBazaar && (
        <PierBazaar
          uid={playerUid}
          playerName={playerName}
          onClose={() => setShowBazaar(false)}
        />
      )}

      {showMarket && (
        <PierMarketplace
          uid={playerUid}
          playerName={playerName}
          mark={liveProfile?.mark}
          initialCounter={showMarket === "minerium" ? "minerium" : "marks"}
          onClose={() => setShowMarket(false)}
        />
      )}

      {/* THE MARK — any uid, read-only. Yours also opens the editor. */}
      {markUid && !editMark && (
        <Mark
          uid={markUid}
          viewingSelf={markUid === playerUid}
          onEdit={markUid === playerUid ? () => setEditMark(true) : undefined}
          onClose={() => setMarkUid(null)}
        />
      )}

      {editMark && (
        <MarkEditor uid={playerUid} onClose={() => setEditMark(false)} />
      )}
    </div>
  );
}
